import React, { useState } from 'react';
import Table from 'react-bootstrap/Table';
import Badge from 'react-bootstrap/Badge';
import { useSwipeable } from 'react-swipeable';
import { isGameFinished, isExcludedFromBestBook } from '../utils/constants';
import { isPotentialOpener, getPreviousProbablePitcher } from '../utils/mlbBullpen';
import NFLDataTable from './NFLDataTable';
import MobileNFLDataTable from './MobileNFLDataTable';
import NCAAFDataTable from './NCAAFDataTable';
import UpgradeButton from './UpgradeButton';
import { WeatherBadge } from './GameHeader';
import { formatGameDayPrefix } from '../utils/formatters';

const DataTable = ({ data, gameTime, gameStatus, gameDate, lastUpdate, game, formatPlayerName, getTeamLogoUrl, getBookLogoUrl, isMobile, selectedMarket, selectedMarketName, bookmakers, isLoading, availableMarkets, onMarketSelection, showTiers, onTierToggle, masterTierFilter, onPlayerClick, onOddsClick, onPlayerContextMenu, clickedCellId, isCollapsed = false, onCollapseToggle, onSettingsClick }) => {
  // Safety check - provide defaults if game data is not fully loaded
  const safeGame = { away: 'TBD', home: 'TBD', gameIDmlb: 'loading', sport: 'mlb', ...game };

  // Helper function to create cell ID for animation
  const createCellId = (gameId, playerId, market, bookmaker, line, side) => {
    return `${gameId}_${playerId}_${market}_${bookmaker}_${line}_${side}`;
  };

  // Helper function to check if cell should be animated
  const getCellClassName = (gameId, playerId, market, bookmaker, line, side) => {
    const cellId = createCellId(gameId, playerId, market, bookmaker, line, side);
    return cellId === clickedCellId ? 'bet-add-animation' : '';
  };

  // Mobile carousel state
  const [mobileBookIndex, setMobileBookIndex] = useState(0);
  const booksPerPage = 5;
  
  // Use provided bookmakers or fall back to default list
  const allBookmakers = bookmakers && bookmakers.length > 0 ? bookmakers : ['fanduel', 'draftkings', 'betmgm', 'fanatics'];
  const totalPages = Math.ceil(allBookmakers.length / booksPerPage);
  
  // Get visible books based on platform
  const bookLogos = isMobile 
    ? allBookmakers.slice(mobileBookIndex * booksPerPage, (mobileBookIndex + 1) * booksPerPage)
    : allBookmakers;
    
  // Navigation functions for mobile
  const [isTransitioning, setIsTransitioning] = useState(false);
  
  const goToPrevBooks = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setMobileBookIndex(prev => (prev === 0 ? totalPages - 1 : prev - 1));
    setTimeout(() => setIsTransitioning(false), 300);
  };
  
  const goToNextBooks = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setMobileBookIndex(prev => (prev === totalPages - 1 ? 0 : prev + 1));
    setTimeout(() => setIsTransitioning(false), 300);
  };
  
  // Swipe handlers for mobile
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => {
      if (isMobile && totalPages > 1) {
        goToNextBooks();
      }
    },
    onSwipedRight: () => {
      if (isMobile && totalPages > 1) {
        goToPrevBooks();
      }
    },
    preventDefaultTouchmoveEvent: true,
    trackMouse: false,
    delta: 50 // Minimum distance for swipe
  });

  // Use sport-specific DataTable components (after all hooks are called)
  if (safeGame.sport === 'nfl') {
    // Use MobileNFLDataTable for mobile devices
    if (isMobile) {
      return (
        <MobileNFLDataTable
          data={data}
          gameTime={gameTime}
          gameStatus={gameStatus}
          game={game}
          formatPlayerName={formatPlayerName}
          getTeamLogoUrl={getTeamLogoUrl}
          getBookLogoUrl={getBookLogoUrl}
          selectedMarket={selectedMarket}
          bookmakers={bookmakers}
          isLoading={isLoading}
          showTiers={showTiers}
          onTierToggle={onTierToggle}
          masterTierFilter={masterTierFilter}
          onOddsClick={onOddsClick}
          clickedCellId={clickedCellId}
          isCollapsed={isCollapsed}
          onCollapseToggle={onCollapseToggle}
          onSettingsClick={onSettingsClick}
        />
      );
    }

    // Use regular NFLDataTable for desktop
    return (
      <NFLDataTable
        data={data}
        gameTime={gameTime}
        gameStatus={gameStatus}
        gameDate={gameDate}
        lastUpdate={lastUpdate}
        game={game}
        formatPlayerName={formatPlayerName}
        getTeamLogoUrl={getTeamLogoUrl}
        getBookLogoUrl={getBookLogoUrl}
        isMobile={isMobile}
        selectedMarket={selectedMarket}
        selectedMarketName={selectedMarketName}
        bookmakers={bookmakers}
        isLoading={isLoading}
        availableMarkets={availableMarkets}
        onMarketSelection={onMarketSelection}
        showTiers={showTiers}
        onTierToggle={onTierToggle}
        masterTierFilter={masterTierFilter}
        onPlayerClick={onPlayerClick}
        onOddsClick={onOddsClick}
        clickedCellId={clickedCellId}
        isCollapsed={isCollapsed}
        onCollapseToggle={onCollapseToggle}
      />
    );
  }

  if (safeGame.sport === 'ncaaf') {
    return (
      <NCAAFDataTable
        data={data}
        gameTime={gameTime}
        gameStatus={gameStatus}
        gameDate={gameDate}
        lastUpdate={lastUpdate}
        game={game}
        formatPlayerName={formatPlayerName}
        getTeamLogoUrl={getTeamLogoUrl}
        getBookLogoUrl={getBookLogoUrl}
        isMobile={isMobile}
        selectedMarket={selectedMarket}
        selectedMarketName={selectedMarketName}
        bookmakers={bookmakers}
        isLoading={isLoading}
        availableMarkets={availableMarkets}
        onMarketSelection={onMarketSelection}
        showTiers={showTiers}
        onTierToggle={onTierToggle}
        masterTierFilter={masterTierFilter}
        onPlayerClick={onPlayerClick}
        onOddsClick={onOddsClick}
        clickedCellId={clickedCellId}
        isCollapsed={isCollapsed}
        onCollapseToggle={onCollapseToggle}
      />
    );
  }

  const oddsCellStyle = { 
    width: '80px',
    minWidth: '80px',
    maxWidth: '80px',
    textAlign: 'center',
    fontSize: '0.95rem',
    letterSpacing: '-0.025em',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
  };
  
  // Calculate mobile column width based on number of bookmakers
  const mobileBookWidth = Math.max(12, Math.floor(60 / bookLogos.length));
  const mobileNameWidth = 100 - (mobileBookWidth * bookLogos.length);

  const formatTime = (timeStr, gameStatus) => {
    if (!timeStr) return '';
    
    // If time is TBD, display TBD
    if (timeStr === 'TBD' || timeStr === 'TBA') return timeStr;
    
    // If game status is Final or Game Over, display the status
    if (isGameFinished(gameStatus)) return gameStatus;
    
    // If game status is Delayed, display Delayed
    if (gameStatus === 'Delayed') {
      return <Badge bg="warning" className="delayed-badge"><span className="delayed-dot"></span>Delayed</Badge>;
    }
    
    // If game status is Postponed, display Postponed
    if (gameStatus === 'Postponed') {
      return <Badge bg="secondary" className="postponed-badge">Postponed</Badge>;
    }
    
    const now = new Date();
    let gameDateTime;
    
    // Use startTimeEST if available (it includes full date and time)
    if (safeGame.startTimeEST) {
      // startTimeEST is typically in format like "2025-01-05T13:00:00-05:00"
      gameDateTime = new Date(safeGame.startTimeEST);
    } else {
      // Fallback to parsing gameDate and timeStr
      const [hour, minute] = timeStr.split(':').map(Number);
      gameDateTime = new Date();
      
      // Parse the game date if available (format: "2025-07-18" or "07/18/2025")
      if (gameDate) {
        // Handle both YYYY-MM-DD and MM/DD/YYYY formats
        if (gameDate.includes('-')) {
          // YYYY-MM-DD format
          const [year, month, day] = gameDate.split('-');
          gameDateTime = new Date(year, month - 1, day, hour, minute, 0, 0);
        } else if (gameDate.includes('/')) {
          // MM/DD/YYYY format
          const [month, day, year] = gameDate.split('/');
          gameDateTime = new Date(year, month - 1, day, hour, minute, 0, 0);
        }
      } else {
        // Fallback to today's date with game time
        gameDateTime.setHours(hour);
        gameDateTime.setMinutes(minute);
        gameDateTime.setSeconds(0);
        gameDateTime.setMilliseconds(0);
      }
    }
    
    // Check if game status is Live
    if (gameStatus === 'Live') {
      // If the game is more than 24 hours away, it's not actually live
      const hoursUntilGame = (gameDateTime - now) / (1000 * 60 * 60);
      if (hoursUntilGame > 24) {
        // Game is days away, just show the time (with weekday/date prefix for >7 days out)
        const options = { hour: 'numeric', minute: 'numeric', hour12: true };
        return `${formatGameDayPrefix(gameDateTime, now)}${gameDateTime.toLocaleTimeString([], options)}`;
      }
      
      // If current time is before game time, show countdown
      if (now < gameDateTime) {
        const diffMs = gameDateTime - now;
        const diffMinutes = Math.ceil(diffMs / (1000 * 60));
        return <Badge bg="warning" className="countdown-badge">Starting in {diffMinutes} min</Badge>;
      }
      
      // Game is actually live
      return <Badge bg="danger" className="live-badge"><span className="live-dot"></span>Live</Badge>;
    }
    
    // Check if the game is today and has started (but status isn't explicitly Live)
    const isToday = gameDate && (
      gameDateTime.toDateString() === now.toDateString()
    );
    
    if (isToday && now > gameDateTime) {
      return <Badge bg="danger" className="live-badge"><span className="live-dot"></span>Live</Badge>;
    }
    
    // Otherwise show scheduled time (with weekday/date prefix for >7 days out)
    const options = { hour: 'numeric', minute: 'numeric', hour12: true };
    return `${formatGameDayPrefix(gameDateTime, now)}${gameDateTime.toLocaleTimeString([], options)}`;
  };

  const formatLastUpdated = (lastUpdate) => {
    if (!lastUpdate) return '';
    
    // Ensure the date is properly parsed - Safari can be strict
    let date;
    
    
    if (typeof lastUpdate === 'string') {
      // Handle ISO date strings
      if (lastUpdate.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
        // If it's a string without timezone info, append 'Z' for UTC
        if (!lastUpdate.includes('Z') && !lastUpdate.includes('+') && !lastUpdate.includes('-')) {
          date = new Date(lastUpdate + 'Z');
        } else {
          date = new Date(lastUpdate);
        }
      } else {
        // Try parsing as-is
        date = new Date(lastUpdate);
      }
    } else if (typeof lastUpdate === 'number') {
      // Unix timestamp - check if seconds or milliseconds
      if (lastUpdate < 10000000000) {
        date = new Date(lastUpdate * 1000); // Convert seconds to milliseconds
      } else {
        date = new Date(lastUpdate);
      }
    } else {
      date = new Date(lastUpdate);
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.error('Invalid date after parsing:', lastUpdate, 'Parsed:', date);
      // Return current time as fallback
      return new Date().toLocaleString('en-US', {
        timeZone: 'America/New_York',
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
        second: 'numeric',
        hour12: true
      });
    }
    
    return date.toLocaleString('en-US', {
      timeZone: 'America/New_York',
      month: 'numeric',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
      hour12: true
    });
  };

  const getCellStyle = (price, bestBookPrice, bestBookDiff, count, direction, book) => {
    const style = {};
    const isExcludedBook = book ? isExcludedFromBestBook(book) : false;

    // Primary best — non-excluded book whose price matches API bestBookPrice
    if (!isExcludedBook && price === bestBookPrice && count > 1) {
      style.fontWeight = 'bold';

      // Apply edge tier colors if they meet the criteria
      if (bestBookDiff !== 1) {
        const diffValue = Math.abs(bestBookDiff);
        if (diffValue >= 0.25) {
          // Tier 1: Green with yellow text (original design)
          Object.assign(style, { backgroundColor: 'green', color: 'yellow' });
        } else if (diffValue >= 0.15) {
          // Tier 2: Amber with navy blue text for better contrast
          Object.assign(style, { backgroundColor: '#F59E0B', color: '#1E40AF' });
        } else if (diffValue >= 0.05) {
          // Tier 3: Blue - subtle, draws less attention than tier 1 & 2
          Object.assign(style, { backgroundColor: '#3B82F6', color: 'white' });
        } else {
          // Best line but below tier thresholds - use tier 2 amber color
          Object.assign(style, { color: '#F59E0B' });
        }
      }
      return style;
    }

    // Secondary highlight — excluded book whose price ties or beats the API best.
    // Outlined-only (no bg fill) so it's visually distinct from the primary.
    // Always render an outline so small edges stay visible.
    if (
      isExcludedBook &&
      price !== null && price !== undefined &&
      bestBookPrice !== null && bestBookPrice !== undefined &&
      ((price > 0 && bestBookPrice > 0 && price >= bestBookPrice) ||
       (price < 0 && bestBookPrice < 0 && price >= bestBookPrice))
    ) {
      let diffValue;
      if (price > bestBookPrice) {
        diffValue = Math.abs((price - bestBookPrice) / bestBookPrice);
      } else if (bestBookDiff !== null && bestBookDiff !== undefined && bestBookDiff !== 1) {
        diffValue = Math.abs(bestBookDiff);
      } else {
        diffValue = 0;
      }
      let caretColor;
      if (diffValue >= 0.25) caretColor = 'green';
      else if (diffValue >= 0.15) caretColor = '#F59E0B';
      else if (diffValue >= 0.05) caretColor = '#3B82F6';
      else caretColor = '#9CA3AF'; // sub-5% / tie — neutral grey
      Object.assign(style, {
        // Corner caret only — no bold, so the cell stays visually quieter than
        // the primary best (which keeps the bg fill + bold).
        backgroundImage: `linear-gradient(225deg, ${caretColor} 7px, transparent 7px)`,
        backgroundRepeat: 'no-repeat',
      });
    }

    return style;
  };

  const getTooltipText = (price, bestBookPrice, bestBookDiff, count, book) => {
    const isExcludedBook = book ? isExcludedFromBestBook(book) : false;
    // Excluded book tying or beating the eligible best — describe its edge vs the
    // primary best so the corner caret has context.
    if (
      isExcludedBook &&
      price !== null && price !== undefined &&
      bestBookPrice !== null && bestBookPrice !== undefined &&
      ((price > 0 && bestBookPrice > 0 && price >= bestBookPrice) ||
       (price < 0 && bestBookPrice < 0 && price >= bestBookPrice))
    ) {
      let diffValue;
      if (price > bestBookPrice) {
        diffValue = Math.abs((price - bestBookPrice) / bestBookPrice);
      } else if (bestBookDiff !== null && bestBookDiff !== undefined && bestBookDiff !== 1) {
        diffValue = Math.abs(bestBookDiff);
      } else {
        diffValue = 0;
      }
      const pct = (diffValue * 100).toFixed(1);
      return price > bestBookPrice
        ? `${pct}% edge over best book`
        : 'Tied with best book';
    }
    const occurrences = [price].filter(p => p === bestBookPrice).length;
    if (price === bestBookPrice && bestBookDiff !== 1 && count > 1 && occurrences === 1) {
      const diffValue = Math.abs(bestBookDiff);
      if (diffValue > 0) {
        return `${(diffValue * 100).toFixed(1)}% edge`;
      }
    }
    return null;
  };

  const renderPrice = (val, direction) => {
    if (val || val === 0) {
      return (
        <span style={{ position: 'relative' }}>
          {val > 0 ? '+' : ''}{val}
          {direction && (
            <span 
              style={{ 
                position: 'absolute', 
                right: '-12px', 
                top: '0px',
                fontSize: '10px',
                color: direction === 'up' ? '#059669' : '#DC2626',
                fontWeight: 'bold'
              }}
            >
              {direction === 'up' ? '↑' : '↓'}
            </span>
          )}
        </span>
      );
    }
    return '\u00A0'; // Non-breaking space to maintain cell height
  };

  if (!data || data.length === 0 || isLoading) {
    // Check if game is locked (subscription required)
    const isLocked = safeGame.gameIDmlb?.startsWith('locked_') || safeGame.gameId?.startsWith('locked_');

    // Show skeleton loading when loading or no data
    const skeletonRows = [1, 2, 3, 4, 5]; // 5 placeholder rows

    return (
      <div className="table-group">
        <div className="game-header-container">
          <img
            src={getTeamLogoUrl(safeGame.away)}
            alt={safeGame.away}
            style={{ width: '32px', height: '32px', marginRight: '8px' }}
          />
          @
          <img
            src={getTeamLogoUrl(safeGame.home)}
            alt={safeGame.home}
            style={{ width: '32px', height: '32px', margin: '0 8px' }}
          />
        </div>

        {/* Market Selection Button Group (skeleton) */}
        <div className="d-flex justify-content-center" style={{ margin: '8px 0 4px 0' }}>
          <div role="group" className="btn-group btn-group-sm">
            {availableMarkets && availableMarkets.map(market => (
              <button
                key={market.key}
                className="btn btn-outline-secondary"
                disabled
                style={{
                  padding: '0.25rem 0.5rem',
                  fontSize: '0.75rem',
                  lineHeight: '1',
                  opacity: '0.5',
                  cursor: 'not-allowed'
                }}
                title={market.name}
              >
                {market.label}
              </button>
            ))}
          </div>
        </div>

        {/* Skeleton Loading */}

        {/* Desktop: Split table skeleton */}
        {!isMobile && (
          <div className="d-flex" style={{ gap: '15px' }}>
            <div style={{ flex: '0 0 120px' }}>
              <div className="table-container">
                <Table bordered size="sm" className="text-center">
                  <thead>
                    <tr><th className="bg-light" style={{ width: '120px' }}>{formatTime(gameTime, gameStatus)}</th></tr>
                    <tr><th className="text-start" style={{ width: '120px' }}>Name</th></tr>
                  </thead>
                  <tbody>
                    {skeletonRows.map(i => (
                      <tr key={`name-${i}`}>
                        <td className="player-name-cell">
                          <div className="skeleton-text"></div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>

            <div style={{ flex: '1' }}>
              <div className="table-container">
                <Table bordered size="sm" className="text-center">
                  <thead>
                    <tr>
                      <th colSpan={bookLogos.length} className="bg-light">
                        {isLoading ? 'Loading...' : (isLocked ? (
                          <UpgradeButton onClick={onSettingsClick} />
                        ) : 'No Data')}
                      </th>
                    </tr>
                    <tr>
                      {bookLogos.map(book => (
                        <th key={`skeleton-${book}`} style={oddsCellStyle}>
                          <img 
                            src={getBookLogoUrl(book)} 
                            alt={`${book} logo`} 
                            title={book} 
                            style={{ height: '20px' }} 
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {skeletonRows.map(i => (
                      <tr key={`odds-${i}`}>
                        {bookLogos.map(book => (
                          <td key={`${book}-${i}`} className="odds-cell" style={oddsCellStyle}>
                            <div className="skeleton-odds"></div>
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          </div>
        )}

        {/* Mobile: Combined skeleton */}
        {isMobile && (
          <div className="table-container">
            <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed' }}>
              <thead>
                <tr>
                  <th className="bg-light" style={{ width: `${mobileNameWidth}%` }}>{formatTime(gameTime, gameStatus)}</th>
                  <th colSpan={bookLogos.length} className="bg-light" style={{ width: `${mobileBookWidth * bookLogos.length}%` }}>
                    {isLoading ? 'Loading...' : (isLocked ? (
                      <UpgradeButton onClick={onSettingsClick} />
                    ) : 'No Data')}
                  </th>
                </tr>
                <tr>
                  <th className="text-start" style={{ width: `${mobileNameWidth}%` }}>Name</th>
                  {bookLogos.map(book => (
                    <th key={`skeleton-${book}`} style={{ width: `${mobileBookWidth}%`, textAlign: 'center' }}>
                      <img 
                        src={getBookLogoUrl(book)} 
                        alt={`${book} logo`} 
                        title={book} 
                        style={{ height: '16px' }} 
                      />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {skeletonRows.map(i => (
                  <tr key={`skeleton-${i}`}>
                    <td 
                      className="player-name-cell"
                      style={{ 
                        width: `${mobileNameWidth}%`,
                        fontSize: '0.8rem'
                      }}
                    >
                      <div className="skeleton-text"></div>
                    </td>
                    {bookLogos.map(book => (
                      <td
                        key={`skeleton-${book}-${i}`}
                        className="odds-cell"
                        style={{
                          width: `${mobileBookWidth}%`,
                          textAlign: 'center',
                          fontSize: '0.75rem'
                        }}
                      >
                        <div className="skeleton-odds"></div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        
        {lastUpdate && (
          <div className="last-updated" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ flex: 1, textAlign: 'left', paddingLeft: '10px' }}>
              {safeGame.sport === 'mlb' && (() => {
                const awayHasLineup = safeGame.awayStartingLineup && safeGame.awayStartingLineup.length > 0;
                const homeHasLineup = safeGame.homeStartingLineup && safeGame.homeStartingLineup.length > 0;
                
                if (awayHasLineup && homeHasLineup) {
                  return 'Lineups confirmed';
                } else if (awayHasLineup) {
                  return `${safeGame.away} lineup confirmed`;
                } else if (homeHasLineup) {
                  return `${safeGame.home} lineup confirmed`;
                } else {
                  return 'Lineups not confirmed';
                }
              })()}
              {safeGame.sport !== 'mlb' && '* Confirmed Starter'}
            </div>
            <div style={{ flex: 1, textAlign: 'right' }}>
              Last updated: {formatLastUpdated(lastUpdate)} ET
            </div>
          </div>
        )}
      </div>
    );
  }

  // Filter data based on showTiers setting and master tier filter
  let filteredData = data;
  
  // Apply master tier filter first (if set)
  if (masterTierFilter !== null) {
    filteredData = filteredData.filter(player => {
      return player.point === parseFloat(masterTierFilter);
    });
  } else if (!['h2h', 'totals', 'spreads'].includes(selectedMarket)) {
    // Apply showTiers filter for all non-mainline markets (including player markets)
    const isYardsMarket = ['player_pass_yds', 'player_rush_yds', 'player_reception_yds', 'player_rush_reception_yds'].includes(selectedMarket);
    const isBasketballStatMarket = ['player_points', 'player_rebounds', 'player_assists', 'player_threes', 'player_points_rebounds_assists', 'player_points_rebounds', 'player_points_assists', 'player_rebounds_assists'].includes(selectedMarket);
    const isMlbPropMarket = selectedMarket.startsWith('batter_') || selectedMarket.startsWith('pitcher_');
    const isBinaryMarket = filteredData.length > 0 && filteredData[0].isBinary;
    // pitcher_outs skips the default MLB point<=1 filter (which would hide
    // every pitcher_outs row since the points are like 14.5+). The downstream
    // O/U render path picks each book's main line per pitcher.
    const isAlwaysAllTiers = selectedMarket === 'pitcher_outs';

    if (isAlwaysAllTiers) {
      // Keep all tiers
    } else if (isYardsMarket || isBasketballStatMarket || isBinaryMarket) {
      // For yards, basketball stat, and binary markets, filter out alternates when tiers are off
      filteredData = showTiers ? filteredData : filteredData.filter(player => !player.isAlternate);
    } else {
      // For MLB props and other player markets, show only point <= 1 when tiers are off
      filteredData = showTiers ? filteredData : filteredData.filter(player => {
        return !player.point || player.point <= 1;
      });
    }
  }
  // For mainline markets (h2h, totals, spreads), show all data by default

  // For pitcher markets, filter out non-starting pitchers if a starter is confirmed
  if (safeGame.sport === 'mlb' && selectedMarket === 'pitcher_strikeouts') {
    // Find starting pitchers (those in lineup at position 10, index 9)
    const getStartingPitchers = () => {
      const starters = [];
      
      if (safeGame.awayStartingLineup && safeGame.awayStartingLineup.length > 9) {
        starters.push({
          playerId: safeGame.awayStartingLineup[9],
          team: 'away'
        });
      }
      
      if (safeGame.homeStartingLineup && safeGame.homeStartingLineup.length > 9) {
        starters.push({
          playerId: safeGame.homeStartingLineup[9],
          team: 'home'
        });
      }
      
      return starters;
    };
    
    const startingPitchers = getStartingPitchers();
    
    if (startingPitchers.length > 0) {
      filteredData = filteredData.filter(player => {
        // Extract numeric ID from player
        const playerIdStr = player.playerId?.toString() || '';
        const numericId = playerIdStr.includes('_') ? 
          parseInt(playerIdStr.split('_')[1]) : 
          parseInt(playerIdStr);
        
        if (isNaN(numericId)) return true;
        
        // Determine player's team
        const teamId = player.teamId || player.team;
        let playerTeam = null;
        
        if (teamId === safeGame.away || teamId === safeGame.awayTeam) {
          playerTeam = 'away';
        } else if (teamId === safeGame.home || teamId === safeGame.homeTeam) {
          playerTeam = 'home';
        }
        
        // Check if there's a starting pitcher confirmed for this team
        const teamHasStarter = startingPitchers.some(starter => 
          starter.team === playerTeam
        );
        
        if (teamHasStarter) {
          // Only show the starting pitcher for this team
          return startingPitchers.some(starter => 
            parseInt(starter.playerId) === numericId
          );
        }
        
        // If no starter confirmed for this team, show all pitchers from this team
        return true;
      });
    }
  }

  // Helper function to check if a player is in the starting lineup
  const isPlayerInLineup = (player) => {
    if (safeGame.sport !== 'mlb' || selectedMarket === 'h2h') {
      return false;
    }
    
    // Extract numeric ID from player.playerId (handles formats like "mlb_672744" or "672744")
    const playerIdStr = player.playerId?.toString() || '';
    const numericId = playerIdStr.includes('_') ? 
      parseInt(playerIdStr.split('_')[1]) : 
      parseInt(playerIdStr);
    
    if (isNaN(numericId)) return false;
    
    // Check away lineup if it exists
    let inAwayLineup = false;
    if (safeGame.awayStartingLineup && safeGame.awayStartingLineup.length > 0) {
      inAwayLineup = safeGame.awayStartingLineup.some(id => parseInt(id) === numericId);
    }
    
    // Check home lineup if it exists
    let inHomeLineup = false;
    if (safeGame.homeStartingLineup && safeGame.homeStartingLineup.length > 0) {
      inHomeLineup = safeGame.homeStartingLineup.some(id => parseInt(id) === numericId);
    }
    
    return inAwayLineup || inHomeLineup;
  };

  // Helper function to determine if player text should be faded
  const shouldFadePlayer = (player) => {
    if (safeGame.sport !== 'mlb' || selectedMarket === 'h2h') {
      return false;
    }
    
    // Don't fade if player is in a lineup
    if (isPlayerInLineup(player)) {
      return false;
    }
    
    // Determine which team the player belongs to
    const candidates = [player.teamId, player.teamAbv, player.team].filter(Boolean);
    let playerTeam = null;
    for (const candidate of candidates) {
      if (candidate === safeGame.away || candidate === safeGame.awayTeam) {
        playerTeam = 'away';
        break;
      } else if (candidate === safeGame.home || candidate === safeGame.homeTeam) {
        playerTeam = 'home';
        break;
      }
    }

    // Only fade if the player's team HAS a lineup (meaning they're not in it)
    // Don't fade if the team doesn't have a lineup yet (we don't know who will start)
    if (playerTeam === 'away') {
      return safeGame.awayStartingLineup && safeGame.awayStartingLineup.length > 0;
    } else if (playerTeam === 'home') {
      return safeGame.homeStartingLineup && safeGame.homeStartingLineup.length > 0;
    }
    
    return false;
  };

  // Helper function to get batting order position for a player (1-based)
  const getBattingOrderPosition = (player) => {
    if (safeGame.sport !== 'mlb' || selectedMarket === 'h2h') {
      return null;
    }
    
    // Don't show position for pitchers (pitcher_strikeouts market)
    if (selectedMarket === 'pitcher_strikeouts') {
      return null;
    }
    
    // Extract numeric ID from player.playerId
    const playerIdStr = player.playerId?.toString() || '';
    const numericId = playerIdStr.includes('_') ? 
      parseInt(playerIdStr.split('_')[1]) : 
      parseInt(playerIdStr);
    
    if (isNaN(numericId)) return null;
    
    // Check away lineup if it exists
    if (safeGame.awayStartingLineup && safeGame.awayStartingLineup.length > 0) {
      const awayIndex = safeGame.awayStartingLineup.findIndex(id => parseInt(id) === numericId);
      if (awayIndex !== -1) {
        return awayIndex + 1; // Convert to 1-based position
      }
    }
    
    // Check home lineup if it exists
    if (safeGame.homeStartingLineup && safeGame.homeStartingLineup.length > 0) {
      const homeIndex = safeGame.homeStartingLineup.findIndex(id => parseInt(id) === numericId);
      if (homeIndex !== -1) {
        return homeIndex + 1; // Convert to 1-based position
      }
    }
    
    return null;
  };

  // Helper function to check if a player name is actually a team defense
  const isTeamDefense = (playerName) => {
    if (!playerName) return false;
    const name = playerName.toLowerCase();
    return name.includes('d/st') || name.includes('defense');
  };

  // Helper function to extract team abbreviation from defense name
  const getTeamFromDefenseName = (playerName) => {
    if (!playerName) return null;
    // Examples: "Buffalo Bills D/ST", "Dallas Cowboys Defense", "NE D/ST"
    const name = playerName.toLowerCase();
    
    // First, check if it's already an abbreviation format (e.g., "NE D/ST")
    const words = playerName.split(' ');
    if (words.length > 0 && words[0].length <= 3 && words[0].match(/^[A-Z]{2,3}$/)) {
      return words[0];
    }
    
    // If the defense name contains one of the game teams, use that team
    const awayTeamLower = safeGame.away?.toLowerCase() || '';
    const homeTeamLower = safeGame.home?.toLowerCase() || '';
    
    if (awayTeamLower && name.includes(awayTeamLower)) {
      return safeGame.away;
    }
    if (homeTeamLower && name.includes(homeTeamLower)) {
      return safeGame.home;
    }
    
    // Common team name mappings for defenses (fallback)
    const teamMappings = {
      'buffalo bills': 'BUF',
      'dallas cowboys': 'DAL', 
      'new england patriots': 'NE',
      'kansas city chiefs': 'KC',
      'green bay packers': 'GB',
      'san francisco 49ers': 'SF',
      'philadelphia eagles': 'PHI',
      'seattle seahawks': 'SEA',
      'los angeles rams': 'LAR',
      'miami dolphins': 'MIA',
      'pittsburgh steelers': 'PIT',
      'baltimore ravens': 'BAL',
      'tennessee titans': 'TEN',
      'indianapolis colts': 'IND',
      'houston texans': 'HOU',
      'jacksonville jaguars': 'JAX',
      'cleveland browns': 'CLE',
      'cincinnati bengals': 'CIN',
      'denver broncos': 'DEN',
      'las vegas raiders': 'LV',
      'los angeles chargers': 'LAC',
      'minnesota vikings': 'MIN',
      'detroit lions': 'DET',
      'chicago bears': 'CHI',
      'atlanta falcons': 'ATL',
      'carolina panthers': 'CAR',
      'new orleans saints': 'NO',
      'tampa bay buccaneers': 'TB',
      'washington commanders': 'WAS',
      'new york giants': 'NYG',
      'arizona cardinals': 'ARI',
      'new york jets': 'NYJ'
    };
    
    // Check for full team names
    for (const [fullName, abbrev] of Object.entries(teamMappings)) {
      if (name.includes(fullName)) {
        return abbrev;
      }
    }
    
    return null;
  };

  // Separate data by market type when h2h is selected
  let h2hData = [];
  let totalsData = [];
  let spreadsData = [];
  
  if (selectedMarket === 'h2h') {
    filteredData.forEach(player => {
      if (player.playerId?.startsWith('totals_')) {
        totalsData.push(player);
      } else if (player.playerId?.startsWith('spreads_')) {
        spreadsData.push(player);
      } else {
        h2hData.push(player);
      }
    });
    
    // Sort each dataset separately
    h2hData.sort((a, b) => {
      if (a.team === 'away' && b.team === 'home') return -1;
      if (a.team === 'home' && b.team === 'away') return 1;
      return 0;
    });
    
    totalsData.sort((a, b) => {
      if (a.point !== b.point) return a.point - b.point;
      if (a.betType === 'over' && b.betType === 'under') return -1;
      if (a.betType === 'under' && b.betType === 'over') return 1;
      return 0;
    });
    
    spreadsData.sort((a, b) => {
      if (a.team !== b.team) {
        if (a.team === 'away' && b.team === 'home') return -1;
        if (a.team === 'home' && b.team === 'away') return 1;
      }
      return a.point - b.point;
    });
  }

  let sortedData = [...filteredData].sort((a, b) => {
    // For h2h market, sort by away team first, then home team
    if (selectedMarket === 'h2h') {
      // Use sortOrder if available (0 for away, 1 for home)
      if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
        return a.sortOrder - b.sortOrder;
      }
      
      // Fallback: sort by team type (away comes before home)
      if (a.team === 'away' && b.team === 'home') return -1;
      if (a.team === 'home' && b.team === 'away') return 1;
      
      return 0;
    }
    
    // For totals and spreads markets, sort by point value first, then by bet type
    if ((selectedMarket === 'totals' || selectedMarket === 'spreads') && a.betType && b.betType) {
      // First sort by point value (ascending for totals, by team for spreads)
      if (selectedMarket === 'spreads') {
        // For spreads, sort by team first (away team first), then by point value
        if (a.betType !== b.betType) {
          if (a.betType === 'away' && b.betType === 'home') return -1;
          if (a.betType === 'home' && b.betType === 'away') return 1;
        }
        
        // Within same team, sort negatives first (descending), then positives (ascending)
        // This gives us: -2.5, -2, -1.5, +1.5, +2, +2.5
        const aIsNegative = a.point < 0;
        const bIsNegative = b.point < 0;
        
        if (aIsNegative && !bIsNegative) return -1; // negative comes before positive
        if (!aIsNegative && bIsNegative) return 1;  // positive comes after negative
        
        if (aIsNegative && bIsNegative) {
          // Both negative: sort descending (-1.5 before -2.5)
          return b.point - a.point;
        } else {
          // Both positive: sort ascending (+1.5 before +2.5)
          return a.point - b.point;
        }
      } else {
        // For totals and yards markets, regular ascending sort
        if (a.point !== b.point) {
          return a.point - b.point;
        }
        // Then by bet type (over before under)
        if (a.betType === 'over' && b.betType === 'under') return -1;
        if (a.betType === 'under' && b.betType === 'over') return 1;
      }
      
      return 0;
    }
    
    // For soccer, sort by team (away first, then home), then by best odds within each team
    if (safeGame.sport === 'soccer') {
      // Determine which team a player belongs to
      const getPlayerTeam = (player) => {
        const playerTeam = player.teamName || player.team;
        if (!playerTeam) return 'unknown';

        // Match against game teams (check full names and abbreviations)
        if (playerTeam === safeGame.away || playerTeam === safeGame.awayTeam ||
            playerTeam === safeGame.awayTeamFull) {
          return 'away';
        }
        if (playerTeam === safeGame.home || playerTeam === safeGame.homeTeam ||
            playerTeam === safeGame.homeTeamFull) {
          return 'home';
        }
        return 'unknown';
      };

      const teamA = getPlayerTeam(a);
      const teamB = getPlayerTeam(b);

      // Sort by team: away first, then home, then unknown
      const teamOrder = { away: 0, home: 1, unknown: 2 };
      if (teamOrder[teamA] !== teamOrder[teamB]) {
        return teamOrder[teamA] - teamOrder[teamB];
      }

      // Within same team, sort by best odds (most favorable first)
      const getBestOdds = (player) => {
        let bestOdds = -9999;
        bookmakers.forEach(book => {
          const odds = player[`${selectedMarket}_${book}`] ?? player[`${selectedMarket}_${book}_over`];
          if (odds !== null && odds !== undefined && odds > bestOdds) {
            bestOdds = odds;
          }
        });
        return bestOdds;
      };

      const oddsA = getBestOdds(a);
      const oddsB = getBestOdds(b);

      // Sort by odds (ascending - worst odds first, best odds last)
      if (oddsA !== oddsB) {
        return oddsA - oddsB;
      }

      // If odds are the same, sort by name
      const nameA = a.name || '';
      const nameB = b.name || '';
      return nameA.localeCompare(nameB);
    }
    
    // For MLB with lineups available (either team can have lineup data)
    if (safeGame.sport === 'mlb' && 
        ((safeGame.awayStartingLineup && safeGame.awayStartingLineup.length > 0) || 
         (safeGame.homeStartingLineup && safeGame.homeStartingLineup.length > 0))) {
      
      // Helper function to get team and lineup position for a player
      const getTeamAndPosition = (player) => {
        // First determine team from player's team fields (check all possible fields)
        const candidates = [player.teamId, player.teamAbv, player.team].filter(Boolean);
        let teamFromField = 'unknown';
        for (const candidate of candidates) {
          if (candidate === safeGame.away || candidate === safeGame.awayTeam) {
            teamFromField = 'away';
            break;
          } else if (candidate === safeGame.home || candidate === safeGame.homeTeam) {
            teamFromField = 'home';
            break;
          }
        }

        // Extract numeric ID from player.playerId (handles formats like "mlb_672744" or "672744")
        const playerIdStr = player.playerId?.toString() || '';
        const numericId = playerIdStr.includes('_') ?
          parseInt(playerIdStr.split('_')[1]) :
          parseInt(playerIdStr);

        if (!isNaN(numericId)) {
          // Check the lineup that matches the player's team first
          const checkLineup = (lineup, lineupTeam) => {
            if (lineup && lineup.length > 0) {
              const idx = lineup.findIndex(id => parseInt(id) === numericId);
              if (idx !== -1) return { team: lineupTeam, position: idx, isInLineup: true };
            }
            return null;
          };

          // If we know the player's team, check that lineup first
          if (teamFromField === 'away') {
            const result = checkLineup(safeGame.awayStartingLineup, 'away');
            if (result) return result;
          } else if (teamFromField === 'home') {
            const result = checkLineup(safeGame.homeStartingLineup, 'home');
            if (result) return result;
          } else {
            // Unknown team - check both lineups
            const awayResult = checkLineup(safeGame.awayStartingLineup, 'away');
            if (awayResult) return awayResult;
            const homeResult = checkLineup(safeGame.homeStartingLineup, 'home');
            if (homeResult) return homeResult;
          }
        }

        return { team: teamFromField, position: 999, isInLineup: false };
      };
      
      const playerAInfo = getTeamAndPosition(a);
      const playerBInfo = getTeamAndPosition(b);
      
      // Sort by team first (away=0, home=1, unknown=2)
      const teamOrder = { away: 0, home: 1, unknown: 2 };
      const aTeamOrder = teamOrder[playerAInfo.team] ?? 2;
      const bTeamOrder = teamOrder[playerBInfo.team] ?? 2;
      if (aTeamOrder !== bTeamOrder) return aTeamOrder - bTeamOrder;
      
      // Within the same team, sort lineup players before non-lineup players
      if (playerAInfo.isInLineup && !playerBInfo.isInLineup) return -1;
      if (!playerAInfo.isInLineup && playerBInfo.isInLineup) return 1;
      
      // If both are in lineup, sort by lineup position
      if (playerAInfo.isInLineup && playerBInfo.isInLineup) {
        if (playerAInfo.position !== playerBInfo.position) {
          return playerAInfo.position - playerBInfo.position;
        }
      }
      
      // If both are not in lineup, sort alphabetically
      const nameA = a.name || '';
      const nameB = b.name || '';
      const nameComparison = nameA.localeCompare(nameB);
      
      // If names are the same, sort by point value
      if (nameComparison === 0) {
        const pointA = a.point || 0;
        const pointB = b.point || 0;
        return pointA - pointB;
      }
      
      return nameComparison;
    }
    
    
    // For MLB without lineups, sort by team (away first) then name
    if (safeGame.sport === 'mlb') {
      const teamA = a.teamAbv || a.team || '';
      const teamB = b.teamAbv || b.team || '';
      const aIsAway = teamA === safeGame.away || teamA === safeGame.awayTeam;
      const bIsAway = teamB === safeGame.away || teamB === safeGame.awayTeam;
      const aIsHome = teamA === safeGame.home || teamA === safeGame.homeTeam;
      const bIsHome = teamB === safeGame.home || teamB === safeGame.homeTeam;
      // Away first, then home, then unknown
      const aOrder = aIsAway ? 0 : aIsHome ? 1 : 2;
      const bOrder = bIsAway ? 0 : bIsHome ? 1 : 2;
      if (aOrder !== bOrder) return aOrder - bOrder;

      const nameA = a.name || '';
      const nameB = b.name || '';
      const nameComparison = nameA.localeCompare(nameB);
      if (nameComparison === 0) {
        return (a.point || 0) - (b.point || 0);
      }
      return nameComparison;
    }

    // For other sports, sort by name first
    const nameA = a.name || '';
    const nameB = b.name || '';
    const nameComparison = nameA.localeCompare(nameB);

    // If names are the same, sort by point value (ascending: 0.5, 1.5, 2.5, etc.)
    if (nameComparison === 0) {
      const pointA = a.point || 0;
      const pointB = b.point || 0;
      return pointA - pointB;
    }

    return nameComparison;
  });

  // pitcher_outs uses the NBA-style O/U layout: each tier entry becomes two
  // rows (Over + Under). The active MLB render path picks the correct odds
  // based on player.betType.
  if (selectedMarket === 'pitcher_outs') {
    sortedData = sortedData.flatMap((entry, i) => {
      const baseKey = entry.tierKey || entry.id || `${entry.name}-${entry.point}-${i}`;
      return [
        { ...entry, betType: 'over', tierKey: `${baseKey}-over` },
        { ...entry, betType: 'under', tierKey: `${baseKey}-under` },
      ];
    });
  }

  return (
    <div className="table-group">
      <div 
        className="game-header-container" 
        style={{ 
          position: 'relative',
          cursor: isMobile ? 'pointer' : 'default',
          userSelect: 'none'
        }}
        onClick={isMobile ? () => onCollapseToggle && onCollapseToggle(!isCollapsed) : undefined}
      >
        {/* Game time/status in top left - always show for collapsed mobile, and always show for NFL */}
        {isMobile && isCollapsed ? (
          <div style={{
            position: 'absolute',
            top: '8px',
            left: '8px',
            fontSize: '0.7rem',
            color: '#6c757d',
            fontWeight: '500'
          }}>
            {formatTime(gameTime, gameStatus)}
          </div>
        ) : null}
        
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          {/* Team logos and scores */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            {(gameStatus !== 'Scheduled' && safeGame.awayScore !== null && safeGame.awayScore !== undefined) && (
              <span style={{ fontSize: isMobile ? '1.5rem' : '2em', fontWeight: 'bold', marginRight: '8px' }}>
                {safeGame.awayScore}
              </span>
            )}
            <img
              src={getTeamLogoUrl(safeGame.away)}
              alt={safeGame.away}
              style={{ width: '32px', height: '32px', marginRight: '8px' }}
            />
            @
            <img
              src={getTeamLogoUrl(safeGame.home)}
              alt={safeGame.home}
              style={{ width: '32px', height: '32px', margin: '0 8px' }}
            />
            {(gameStatus !== 'Scheduled' && safeGame.homeScore !== null && safeGame.homeScore !== undefined) && (
              <span style={{ fontSize: isMobile ? '1.5rem' : '2em', fontWeight: 'bold', marginLeft: '8px' }}>
                {safeGame.homeScore}
              </span>
            )}
            {/* Collapse indicator for mobile */}
            {isMobile && (
              <span style={{ 
                marginLeft: '12px', 
                fontSize: '1.2rem',
                color: '#6c757d',
                transition: 'transform 0.2s ease'
              }}>
                {isCollapsed ? '▼' : '▲'}
              </span>
            )}
          </div>
          
          {/* Probable pitchers for MLB pre-game or full team names for WNBA/Soccer/NCAAF */}
          {((gameStatus !== 'Live' && !isGameFinished(gameStatus)) || safeGame.sport === 'ncaaf' || safeGame.sport === 'wnba' || safeGame.sport === 'soccer') && (
            <div style={{ 
              fontSize: isMobile ? '0.9rem' : '0.75rem', 
              fontWeight: isMobile ? 'bold' : 'normal',
              color: '#6c757d', 
              textAlign: 'center' 
            }}>
              {(safeGame.sport === 'wnba' || safeGame.sport === 'soccer' || safeGame.sport === 'ncaaf') ? (
                // Show full team names for WNBA, Soccer, and NCAAF
                `${safeGame.awayTeamFull || safeGame.away} vs ${safeGame.homeTeamFull || safeGame.home}`
              ) : (
                // Show probable pitchers for MLB (only pre-game). When a side
                // has a potential opener the previously-listed probable is
                // shown underneath in parentheses ("the true starter").
                (safeGame.awayProbablePitcher || safeGame.homeProbablePitcher) && (() => {
                  const awayIsOpener = isPotentialOpener(safeGame, false);
                  const homeIsOpener = isPotentialOpener(safeGame, true);
                  const awayPrev = awayIsOpener ? getPreviousProbablePitcher(safeGame, false) : null;
                  const homePrev = homeIsOpener ? getPreviousProbablePitcher(safeGame, true) : null;
                  const openPitcher = (pitcher, isHomeSide) => {
                    if (!onPlayerClick || !pitcher?.id) return;
                    onPlayerClick(
                      safeGame.gameIDmlb,
                      `mlb_${pitcher.id}`,
                      pitcher.fullName,
                      selectedMarket,
                      safeGame.away,
                      safeGame.home,
                      isHomeSide ? safeGame.home : safeGame.away,
                      'pitcher'
                    );
                  };
                  const pitcherNameStyle = {
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textDecorationStyle: 'dotted',
                    textUnderlineOffset: '2px',
                  };
                  const renderStarter = (pitcher, isOpener, isHomeSide) => (
                    <>
                      {isOpener && <sup>1</sup>}
                      {pitcher?.id ? (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); openPitcher(pitcher, isHomeSide); }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); openPitcher(pitcher, isHomeSide); } }}
                          style={pitcherNameStyle}
                        >
                          {pitcher.fullName}
                        </span>
                      ) : (pitcher?.fullName || 'TBD')}
                      {isOpener ? '*' : ''}
                      {pitcher?.throws ? ` (${pitcher.throws})` : ''}
                    </>
                  );
                  const renderPrev = (prev, isHomeSide) => (
                    <>
                      <sup>2</sup>
                      {prev.id ? (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => { e.stopPropagation(); openPitcher(prev, isHomeSide); }}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); openPitcher(prev, isHomeSide); } }}
                          style={pitcherNameStyle}
                        >
                          {prev.fullName}
                        </span>
                      ) : prev.fullName}
                      {prev.throws ? ` (${prev.throws})` : ''}
                    </>
                  );
                  // Grid so the second-row "(prev)" text sits directly under
                  // its team's starter instead of centered as a single string.
                  return (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', columnGap: '6px', alignItems: 'baseline', justifyItems: 'center' }}>
                      <div style={{ textAlign: 'right', justifySelf: 'end' }}>
                        {renderStarter(safeGame.awayProbablePitcher, awayIsOpener, false)}
                      </div>
                      <div>vs</div>
                      <div style={{ textAlign: 'left', justifySelf: 'start' }}>
                        {renderStarter(safeGame.homeProbablePitcher, homeIsOpener, true)}
                      </div>
                      {(awayPrev || homePrev) && (
                        <>
                          <div style={{ textAlign: 'right', justifySelf: 'end', opacity: 0.85, marginTop: '2px' }}>
                            {awayPrev ? renderPrev(awayPrev, false) : ''}
                          </div>
                          <div></div>
                          <div style={{ textAlign: 'left', justifySelf: 'start', opacity: 0.85, marginTop: '2px' }}>
                            {homePrev ? renderPrev(homePrev, true) : ''}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()
              )}
            </div>
          )}
        </div>
        
      </div>

      {/* Market Selection and Tier Toggle - Hide on mobile when collapsed */}
      {!(isMobile && isCollapsed) && !(isMobile && safeGame.sport === 'mlb') && (
        <div className="d-flex justify-content-center align-items-center flex-wrap" style={{ margin: '8px 0 4px 0', gap: '12px' }}>
          {isMobile ? (
            /* Mobile: Two rows - mainlines first, then props */
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {/* Row 1: Mainline Markets */}
              {availableMarkets && availableMarkets.some(m => m.isMainline) && (
                <div role="group" className="btn-group btn-group-sm">
                  {availableMarkets.filter(m => m.isMainline).map(market => (
                    <React.Fragment key={market.key}>
                      <input
                        className="btn-check"
                        type="radio"
                        name={`market-${safeGame.gameIDmlb}`}
                        id={`${market.key}-${safeGame.gameIDmlb}`}
                        value={market.key}
                        checked={selectedMarket === market.key}
                        onChange={() => onMarketSelection(safeGame.gameIDmlb, market.key)}
                        autoComplete="off"
                      />
                      <label
                        htmlFor={`${market.key}-${safeGame.gameIDmlb}`}
                        className={`btn ${selectedMarket === market.key ? 'btn-primary' : 'btn-outline-primary'}`}
                        title={market.name}
                        style={{ 
                          padding: '0.25rem 0.5rem',
                          fontSize: '0.75rem',
                          lineHeight: '1'
                        }}
                      >
                        {market.label}
                      </label>
                    </React.Fragment>
                  ))}
                </div>
              )}
              
              {/* Row 2: Player Markets */}
              <div role="group" className="btn-group btn-group-sm flex-wrap">
                {availableMarkets && availableMarkets.filter(m => !m.isMainline).map(market => (
                  <React.Fragment key={market.key}>
                    <input
                      className="btn-check"
                      type="radio"
                      name={`market-${safeGame.gameIDmlb}`}
                      id={`${market.key}-${safeGame.gameIDmlb}`}
                      value={market.key}
                      checked={selectedMarket === market.key}
                      onChange={() => onMarketSelection(safeGame.gameIDmlb, market.key)}
                      autoComplete="off"
                    />
                    <label
                      htmlFor={`${market.key}-${safeGame.gameIDmlb}`}
                      className={`btn ${selectedMarket === market.key ? 'btn-primary' : 'btn-outline-primary'}`}
                      title={market.name}
                      style={{ 
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        lineHeight: '1'
                      }}
                    >
                      {market.label}
                    </label>
                  </React.Fragment>
                ))}
              </div>
            </div>
          ) : (
            /* Desktop: Single row with all markets */
            <div role="group" className="btn-group btn-group-sm">
              {availableMarkets && availableMarkets.map(market => (
                <React.Fragment key={market.key}>
                  <input
                    className="btn-check"
                    type="radio"
                    name={`market-${safeGame.gameIDmlb}`}
                    id={`${market.key}-${safeGame.gameIDmlb}`}
                    value={market.key}
                    checked={selectedMarket === market.key}
                    onChange={() => onMarketSelection(safeGame.gameIDmlb, market.key)}
                    autoComplete="off"
                  />
                  <label
                    htmlFor={`${market.key}-${safeGame.gameIDmlb}`}
                    className={`btn ${selectedMarket === market.key ? 'btn-primary' : 'btn-outline-primary'}`}
                    title={market.name}
                    style={{ 
                      padding: '0.25rem 0.5rem',
                      fontSize: '0.75rem',
                      lineHeight: '1'
                    }}
                  >
                    {market.label}
                  </label>
                </React.Fragment>
              ))}
            </div>
          )}
          
          {/* Game-specific Tier Toggle */}
          <div className="form-check form-switch">
            <input
              className="form-check-input"
              type="checkbox"
              id={`tier-toggle-${safeGame.gameIDmlb}`}
              checked={selectedMarket.includes('first_basket') || selectedMarket === 'h2h' ? false : (selectedMarket === 'pitcher_outs' ? true : (showTiers || false))}
              onChange={(e) => onTierToggle && onTierToggle(e.target.checked)}
              style={{ height: '1rem', width: '2rem' }}
              disabled={masterTierFilter !== null || selectedMarket.includes('first_basket') || selectedMarket === 'h2h' || selectedMarket === 'pitcher_outs'}
            />
            <label
              className="form-check-label text-muted"
              htmlFor={`tier-toggle-${safeGame.gameIDmlb}`}
              style={{
                fontSize: '0.75rem',
                opacity: (masterTierFilter !== null || selectedMarket.includes('first_basket') || selectedMarket === 'h2h' || selectedMarket === 'pitcher_outs') ? 0.5 : 1
              }}
            >
              Tiers
            </label>
          </div>
        </div>
      )}

      {/* Desktop: Split table design with separate name column */}
      {!isMobile && selectedMarket === 'h2h' && (h2hData.length > 0 || totalsData.length > 0 || spreadsData.length > 0) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          {/* Moneyline Table */}
          {h2hData.length > 0 && (
            <div>
              <div style={{ flex: '1' }}>
                <div className="table-container">
                  <Table bordered size="sm" className="text-center">
                    <thead>
                      <tr><th colSpan={bookLogos.length + 1} className="bg-light">Moneyline</th></tr>
                      <tr>
                        <th style={{ width: '120px' }}></th>
                        {bookLogos.map(book => (
                          <th key={`h2h-${book}`} style={oddsCellStyle}>
                            <img 
                              src={getBookLogoUrl(book)} 
                              alt={`${book} logo`} 
                              title={book} 
                              style={{ height: '20px' }} 
                            />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {h2hData.map((player) => {
                        const count = bookLogos.filter(book => 
                          player[`h2h_${book}`] !== null && player[`h2h_${book}`] !== undefined
                        ).length;
                        
                        return (
                          <tr key={player.tierKey || player.id}>
                            <td style={{ width: '120px', fontWeight: 'bold' }}>
                              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                <img
                                  src={getTeamLogoUrl(player.team === 'away' ? safeGame.away : safeGame.home)}
                                  alt={player.team === 'away' ? safeGame.away : safeGame.home}
                                  style={{ width: '20px', height: '20px' }}
                                />
                                <span>{player.team === 'away' ? (safeGame.awayNickname || safeGame.away) : (safeGame.homeNickname || safeGame.home)}</span>
                              </div>
                            </td>
                            {bookLogos.map(book => (
                              <td
                                key={`h2h-${book}`}
                                className={`odds-cell ${getCellClassName(safeGame.gameIDmlb, player.playerId, 'h2h', book, null, 'moneyline')}`}
                                style={{
                                  ...getCellStyle(
                                    player[`h2h_${book}`],
                                    player[`h2h_bestBookPrice`],
                                    player[`h2h_bestBookDiff`],
                                    count,
                                    player[`h2h_${book}_direction`],
                                    book
                                  ),
                                  ...oddsCellStyle,
                                  fontSize: '1rem',
                                  cursor: player[`h2h_${book}`] ? 'pointer' : 'default',
                                  opacity: player[`h2h_${book}_isAvailable`] === false ? 0.25 : undefined
                                }}
                                onClick={() => {
                                  if (onOddsClick && player[`h2h_${book}`]) {
                                    onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, 'h2h', book, player[`h2h_${book}`], safeGame.away, safeGame.home, player.team || player.name, null, null, 'moneyline', (player.team || player.name) === safeGame.home ? safeGame.homeTeamId : safeGame.awayTeamId);
                                  }
                                }}
                              >
                                {renderPrice(player[`h2h_${book}`], player[`h2h_${book}_direction`])}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          {/* Totals Table */}
          {totalsData.length > 0 && (
            <div>
              <div style={{ flex: '1' }}>
                <div className="table-container">
                  <Table bordered size="sm" className="text-center">
                    <thead>
                      <tr><th colSpan={bookLogos.length + 1} className="bg-light">Totals</th></tr>
                      <tr>
                        <th style={{ width: '120px' }}></th>
                        {bookLogos.map(book => (
                          <th key={`totals-${book}`} style={oddsCellStyle}>
                            <img 
                              src={getBookLogoUrl(book)} 
                              alt={`${book} logo`} 
                              title={book} 
                              style={{ height: '20px' }} 
                            />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {/* Over Row */}
                      <tr>
                        <td style={{ width: '120px', fontWeight: 'bold' }}>Over</td>
                        {bookLogos.map(book => {
                          // Find all over entries and group them by point value
                          const overEntries = totalsData.filter(p => p.betType === 'over');
                          let cellContent = [];
                          
                          overEntries.forEach(player => {
                            const odds = player[`totals_${book}`];
                            if (odds !== null && odds !== undefined) {
                              cellContent.push({
                                point: player.point,
                                odds: odds,
                                player: player
                              });
                            }
                          });
                          
                          // Sort by point value
                          cellContent.sort((a, b) => a.point - b.point);
                          
                          return (
                            <td
                              key={`totals-over-${book}`}
                              className={`odds-cell ${cellContent.length > 0 && cellContent[0] ? getCellClassName(safeGame.gameIDmlb, cellContent[0].player.playerId, 'totals', book, cellContent[0].point, 'over') : ''}`}
                              style={oddsCellStyle}
                            >
                              {cellContent.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  {cellContent.map(({ point, odds, player }) => (
                                    <div
                                      key={`over-${point}`}
                                      className={getCellClassName(safeGame.gameIDmlb, player.playerId, 'totals', book, point, 'over')}
                                      style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        borderBottom: cellContent.length > 1 ? '1px solid #dee2e6' : 'none',
                                        paddingBottom: cellContent.length > 1 ? '2px' : '0',
                                        opacity: player[`totals_${book}_over_isAvailable`] === false ? 0.25 : undefined
                                      }}
                                      onClick={() => {
                                        if (onOddsClick && odds) {
                                          onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, 'totals', book, odds, safeGame.away, safeGame.home, player.team || player.name, null, point, 'over');
                                        }
                                      }}
                                    >
                                      <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#9CA3AF', marginBottom: '2px' }}>O {point}</span>
                                      <span style={{ fontSize: '1rem' }}>{odds > 0 ? '+' : ''}{odds}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : '\u00A0'}
                            </td>
                          );
                        })}
                      </tr>
                      
                      {/* Under Row */}
                      <tr>
                        <td style={{ width: '120px', fontWeight: 'bold' }}>Under</td>
                        {bookLogos.map(book => {
                          // Find all under entries and group them by point value
                          const underEntries = totalsData.filter(p => p.betType === 'under');
                          let cellContent = [];
                          
                          underEntries.forEach(player => {
                            const odds = player[`totals_${book}`];
                            if (odds !== null && odds !== undefined) {
                              cellContent.push({
                                point: player.point,
                                odds: odds,
                                player: player
                              });
                            }
                          });
                          
                          // Sort by point value
                          cellContent.sort((a, b) => a.point - b.point);
                          
                          return (
                            <td
                              key={`totals-under-${book}`}
                              className={`odds-cell ${cellContent.length > 0 && cellContent[0] ? getCellClassName(safeGame.gameIDmlb, cellContent[0].player.playerId, 'totals', book, cellContent[0].point, 'under') : ''}`}
                              style={oddsCellStyle}
                            >
                              {cellContent.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  {cellContent.map(({ point, odds, player }) => (
                                    <div
                                      key={`under-${point}`}
                                      className={getCellClassName(safeGame.gameIDmlb, player.playerId, 'totals', book, point, 'under')}
                                      style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        borderBottom: cellContent.length > 1 ? '1px solid #dee2e6' : 'none',
                                        paddingBottom: cellContent.length > 1 ? '2px' : '0',
                                        opacity: player[`totals_${book}_under_isAvailable`] === false ? 0.25 : undefined
                                      }}
                                      onClick={() => {
                                        if (onOddsClick && odds) {
                                          onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, 'totals', book, odds, safeGame.away, safeGame.home, player.team || player.name, null, point, 'under');
                                        }
                                      }}
                                    >
                                      <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#9CA3AF', marginBottom: '2px' }}>U {point}</span>
                                      <span style={{ fontSize: '1rem' }}>{odds > 0 ? '+' : ''}{odds}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : '\u00A0'}
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>
          )}

          {/* Spreads Table */}
          {spreadsData.length > 0 && (
            <div>
              <div style={{ flex: '1' }}>
                <div className="table-container">
                  <Table bordered size="sm" className="text-center">
                    <thead>
                      <tr><th colSpan={bookLogos.length + 1} className="bg-light">Spreads</th></tr>
                      <tr>
                        <th style={{ width: '120px' }}></th>
                        {bookLogos.map(book => (
                          <th key={`spreads-${book}`} style={oddsCellStyle}>
                            <img 
                              src={getBookLogoUrl(book)} 
                              alt={`${book} logo`} 
                              title={book} 
                              style={{ height: '20px' }} 
                            />
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {/* Away Team Row */}
                      <tr>
                        <td style={{ width: '120px', fontWeight: 'bold' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                            <img
                              src={getTeamLogoUrl(safeGame.away)}
                              alt={safeGame.away}
                              style={{ width: '16px', height: '16px' }}
                            />
                            <span>{safeGame.awayNickname || safeGame.away}</span>
                          </div>
                        </td>
                        {bookLogos.map(book => {
                          // Find all away team entries and group them by point value
                          const awayEntries = spreadsData.filter(p => p.team === 'away');
                          let cellContent = [];

                          awayEntries.forEach(player => {
                            const odds = player[`spreads_${book}`];
                            if (odds !== null && odds !== undefined) {
                              cellContent.push({
                                point: player.point,
                                odds: odds,
                                player: player
                              });
                            }
                          });

                          // Sort by point value
                          cellContent.sort((a, b) => a.point - b.point);

                          return (
                            <td
                              key={`spreads-away-${book}`}
                              className={`odds-cell ${cellContent.length > 0 && cellContent[0] ? getCellClassName(safeGame.gameIDmlb, cellContent[0].player.playerId, 'spreads', book, cellContent[0].point, 'spread') : ''}`}
                              style={oddsCellStyle}
                            >
                              {cellContent.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  {cellContent.map(({ point, odds, player }) => (
                                    <div
                                      key={`away-${point}`}
                                      className={getCellClassName(safeGame.gameIDmlb, player.playerId, 'spreads', book, point, 'spread')}
                                      style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        borderBottom: cellContent.length > 1 ? '1px solid #dee2e6' : 'none',
                                        paddingBottom: cellContent.length > 1 ? '2px' : '0',
                                        opacity: player[`spreads_${book}_away_isAvailable`] === false ? 0.25 : undefined
                                      }}
                                      onClick={() => {
                                        if (onOddsClick && odds) {
                                          onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, 'spreads', book, odds, safeGame.away, safeGame.home, safeGame.away, null, point, 'spread', safeGame.awayTeamId);
                                        }
                                      }}
                                    >
                                      <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#9CA3AF', marginBottom: '2px' }}>{point > 0 ? `+${point}` : `${point}`}</span>
                                      <span style={{ fontSize: '1rem' }}>{odds > 0 ? '+' : ''}{odds}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : '\u00A0'}
                            </td>
                          );
                        })}
                      </tr>
                      
                      {/* Home Team Row */}
                      <tr>
                        <td style={{ width: '120px', fontWeight: 'bold' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                            <img
                              src={getTeamLogoUrl(safeGame.home)}
                              alt={safeGame.home}
                              style={{ width: '16px', height: '16px' }}
                            />
                            <span>{safeGame.homeNickname || safeGame.home}</span>
                          </div>
                        </td>
                        {bookLogos.map(book => {
                          // Find all home team entries and group them by point value
                          const homeEntries = spreadsData.filter(p => p.team === 'home');
                          let cellContent = [];

                          homeEntries.forEach(player => {
                            const odds = player[`spreads_${book}`];
                            if (odds !== null && odds !== undefined) {
                              cellContent.push({
                                point: player.point,
                                odds: odds,
                                player: player
                              });
                            }
                          });

                          // Sort by point value
                          cellContent.sort((a, b) => a.point - b.point);

                          return (
                            <td
                              key={`spreads-home-${book}`}
                              className={`odds-cell ${cellContent.length > 0 && cellContent[0] ? getCellClassName(safeGame.gameIDmlb, cellContent[0].player.playerId, 'spreads', book, cellContent[0].point, 'spread') : ''}`}
                              style={oddsCellStyle}
                            >
                              {cellContent.length > 0 ? (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                  {cellContent.map(({ point, odds, player }) => (
                                    <div
                                      key={`home-${point}`}
                                      className={getCellClassName(safeGame.gameIDmlb, player.playerId, 'spreads', book, point, 'spread')}
                                      style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        borderBottom: cellContent.length > 1 ? '1px solid #dee2e6' : 'none',
                                        paddingBottom: cellContent.length > 1 ? '2px' : '0',
                                        opacity: player[`spreads_${book}_home_isAvailable`] === false ? 0.25 : undefined
                                      }}
                                      onClick={() => {
                                        if (onOddsClick && odds) {
                                          onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, 'spreads', book, odds, safeGame.away, safeGame.home, safeGame.home, null, point, 'spread', safeGame.homeTeamId);
                                        }
                                      }}
                                    >
                                      <span style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#9CA3AF', marginBottom: '2px' }}>{point > 0 ? `+${point}` : `${point}`}</span>
                                      <span style={{ fontSize: '1rem' }}>{odds > 0 ? '+' : ''}{odds}</span>
                                    </div>
                                  ))}
                                </div>
                              ) : '\u00A0'}
                            </td>
                          );
                        })}
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* NFL desktop section removed - handled by NFLDataTable */}
      {false && (
        <div className="table-container">
          <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th style={{ width: '200px' }}>NAME</th>
                <th colSpan={bookLogos.length}>{selectedMarketName.toUpperCase()}</th>
              </tr>
              <tr>
                <th>PLAYER</th>
                {bookLogos.map(book => (
                  <th key={`${selectedMarket}-${book}`} style={{ width: '60px', minWidth: '60px', maxWidth: '60px', textAlign: 'center', fontSize: '0.95rem', letterSpacing: '-0.025em', transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)' }}>
                    <img 
                      src={getBookLogoUrl(book)} 
                      alt={`${book} logo`} 
                      title={book} 
                      style={{ height: '20px' }} 
                    />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(() => {
                // Debug the raw data structure
                if (selectedMarket === 'player_pass_yds' && sortedData.length > 0) {
                  console.log('Raw sortedData for Pass Yards:', sortedData.slice(0, 5));
                  console.log('Available books from first entry:', 
                    Object.keys(sortedData[0]).filter(k => k.includes('_over') || k.includes('_under'))
                  );
                }
                
                // For yards markets and basketball stat markets, separate non-alternate and alternate lines
                const isYardsMarket = ['player_pass_yds', 'player_rush_yds', 'player_reception_yds', 'player_rush_reception_yds'].includes(selectedMarket);
                const isBasketballStatMarket = ['player_points', 'player_rebounds', 'player_assists', 'player_threes', 'player_points_rebounds_assists', 'player_points_rebounds', 'player_points_assists', 'player_rebounds_assists'].includes(selectedMarket);
                // MLB markets that should use the NBA/WNBA O/U two-row layout.
                const isMlbOuMarket = selectedMarket === 'pitcher_outs';

                // Debug basketball markets
                if (isBasketballStatMarket && sortedData.length > 0) {
                  console.log('Basketball market detected in DataTable:', {
                    market: selectedMarket,
                    isBasketballStatMarket,
                    samplePlayer: sortedData[0],
                    playerKeys: Object.keys(sortedData[0]).filter(k => k.includes('over') || k.includes('under'))
                  });
                }

                // Bucket 1: Non-alternate values only
                // Bucket 2: Alternate values (for future use)
                const nonAlternateData = (isYardsMarket || isBasketballStatMarket || isMlbOuMarket) ? sortedData.filter(p => !p.isAlternate) : sortedData;
                const alternateData = (isYardsMarket || isBasketballStatMarket || isMlbOuMarket) ? sortedData.filter(p => p.isAlternate) : [];

                if ((isYardsMarket || isBasketballStatMarket || isMlbOuMarket) && nonAlternateData.length > 0) {
                  console.log('Non-alternate data sample:', nonAlternateData.slice(0, 1));
                }
                
                // Group players by team - only use non-alternate data for yards markets
                const playerDataByName = {};
                nonAlternateData
                  .filter(p => p.team === safeGame.away)
                  .forEach(player => {
                    if (!playerDataByName[player.name]) {
                      playerDataByName[player.name] = {
                        name: player.name,
                        team: player.team,
                        playerId: player.playerId,
                        teamId: player.teamId,
                        starter: player.starter,
                        positionCode: player.positionCode,
                        position: player.position,
                        point: player.point
                      };
                    }
                    
                    // Store each bookmaker's odds with their specific point value
                    // Since we're only looking at non-alternates, each book can have different main lines
                    bookLogos.forEach(book => {
                      const overKey = `${selectedMarket}_${book}_over`;
                      const underKey = `${selectedMarket}_${book}_under`;
                      const pointKey = `${selectedMarket}_${book}_point`;
                      
                      if (player[overKey] !== undefined && player[overKey] !== null) {
                        // For non-alternates, just store the first one we see for each book
                        if (!playerDataByName[player.name][overKey]) {
                          playerDataByName[player.name][overKey] = player[overKey];
                          playerDataByName[player.name][underKey] = player[underKey];
                          playerDataByName[player.name][pointKey] = player.point;
                        }
                      }
                    });
                  });
                
                const awayPlayers = Object.values(playerDataByName);
                
                // Debug final merged data
                const testPlayer = awayPlayers.find(p => p.name === 'Dak Prescott');
                if (testPlayer) {
                  console.log('Final Dak data:', {
                    name: testPlayer.name,
                    fanduel: {
                      over: testPlayer.player_pass_yds_fanduel_over,
                      under: testPlayer.player_pass_yds_fanduel_under,
                      point: testPlayer.player_pass_yds_fanduel_point
                    },
                    draftkings: {
                      over: testPlayer.player_pass_yds_draftkings_over,
                      under: testPlayer.player_pass_yds_draftkings_under,
                      point: testPlayer.player_pass_yds_draftkings_point
                    }
                  });
                }
                  
                // Group home players the same way - using only non-alternate data
                const homePlayerDataByName = {};
                nonAlternateData
                  .filter(p => p.team === safeGame.home)
                  .forEach(player => {
                    if (!homePlayerDataByName[player.name]) {
                      homePlayerDataByName[player.name] = {
                        name: player.name,
                        team: player.team,
                        playerId: player.playerId,
                        teamId: player.teamId,
                        starter: player.starter,
                        positionCode: player.positionCode,
                        position: player.position,
                        point: player.point
                      };
                    }
                    
                    // Store each bookmaker's odds with their specific point value
                    // Since we're only looking at non-alternates, each book can have different main lines
                    bookLogos.forEach(book => {
                      const overKey = `${selectedMarket}_${book}_over`;
                      const underKey = `${selectedMarket}_${book}_under`;
                      const pointKey = `${selectedMarket}_${book}_point`;
                      
                      if (player[overKey] !== undefined && player[overKey] !== null) {
                        // For non-alternates, just store the first one we see for each book
                        if (!homePlayerDataByName[player.name][overKey]) {
                          homePlayerDataByName[player.name][overKey] = player[overKey];
                          homePlayerDataByName[player.name][underKey] = player[underKey];
                          homePlayerDataByName[player.name][pointKey] = player.point;
                        }
                      }
                    });
                  });
                
                const homePlayers = Object.values(homePlayerDataByName);
                
                // Debug: Log final merged data
                const dakInAway = awayPlayers.find(p => p.name === 'Dak Prescott');
                if (dakInAway) {
                  console.log('Final Dak Prescott data (away):', {
                    point: dakInAway.point,
                    allOdds: bookLogos.map(book => ({
                      book,
                      over: dakInAway[`${selectedMarket}_${book}_over`],
                      under: dakInAway[`${selectedMarket}_${book}_under`]
                    })),
                    allKeys: Object.keys(dakInAway).filter(k => k.includes('_over') || k.includes('_under'))
                  });
                }
                
                return (
                  <>
                    {/* Away Team Players */}
                    {awayPlayers.map((player) => {
                      const isYardsMarket = ['player_pass_yds', 'player_rush_yds', 'player_reception_yds', 'player_rush_reception_yds'].includes(selectedMarket);
                      const isBasketballStatMarket = ['player_points', 'player_rebounds', 'player_assists', 'player_threes', 'player_points_rebounds_assists', 'player_points_rebounds', 'player_points_assists', 'player_rebounds_assists'].includes(selectedMarket);
                      const isMlbOuMarket = selectedMarket === 'pitcher_outs';

                      if (isYardsMarket || isBasketballStatMarket || isMlbOuMarket) {
                        // For yards markets and basketball stat markets, create two rows per player
                        return (
                          <React.Fragment key={`away-${player.playerId || player.name}-${player.point}`}>
                            {/* Over row */}
                            <tr>
                              <td rowSpan={2} style={{ textAlign: 'left', padding: '4px 8px' }}>
                                <span 
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    if (onPlayerClick) {
                                      onPlayerClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, safeGame.away, safeGame.home);
                                    }
                                  }}
                                >
                                  <span style={{ 
                                    color: shouldFadePlayer(player) ? '#9CA3AF' : 'inherit',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}>
                                    <img
                                      src={getTeamLogoUrl(safeGame.away)}
                                      alt={safeGame.away}
                                      style={{ width: '16px', height: '16px' }}
                                    />
                                    {formatPlayerName(player.name, isPlayerInLineup(player), safeGame.sport)}
                                  </span>
                                </span>
                              </td>
                              {bookLogos.map(book => {
                                const overOdds = player[`${selectedMarket}_${book}_over`];
                                const bookPoint = player[`${selectedMarket}_${book}_point`] || player.point;
                                
                                return (
                                  <td
                                    key={`${selectedMarket}-${book}-over`}
                                    className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, book, bookPoint, 'over')}
                                    onClick={() => {
                                      if (overOdds && onOddsClick) {
                                        onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, overOdds, safeGame.away, safeGame.home, player.team, player.position, bookPoint, 'over');
                                      }
                                    }}
                                    style={{
                                      textAlign: 'center',
                                      padding: '8px 4px',
                                      cursor: overOdds ? 'pointer' : 'default',
                                      opacity: shouldFadePlayer(player) ? 0.5 : 1
                                    }}
                                  >
                                    {overOdds !== null && overOdds !== undefined ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                          {overOdds > 0 ? `+${overOdds}` : overOdds}
                                        </span>
                                        <span style={{ fontSize: '0.65rem', color: '#6B7280' }}>
                                          o{bookPoint}
                                        </span>
                                      </div>
                                    ) : (
                                      <span style={{ color: '#9CA3AF' }}>-</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                            {/* Under row */}
                            <tr>
                              {bookLogos.map(book => {
                                const underOdds = player[`${selectedMarket}_${book}_under`];
                                const bookPoint = player[`${selectedMarket}_${book}_point`] || player.point;
                                
                                return (
                                  <td
                                    key={`${selectedMarket}-${book}-under`}
                                    className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, book, bookPoint, 'under')}
                                    onClick={() => {
                                      if (underOdds && onOddsClick) {
                                        onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, underOdds, safeGame.away, safeGame.home, player.team, player.position, bookPoint, 'under');
                                      }
                                    }}
                                    style={{
                                      textAlign: 'center',
                                      padding: '8px 4px',
                                      cursor: underOdds ? 'pointer' : 'default',
                                      opacity: shouldFadePlayer(player) ? 0.5 : 1
                                    }}
                                  >
                                    {underOdds !== null && underOdds !== undefined ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                                        <span style={{ fontSize: '0.65rem', color: '#6B7280' }}>
                                          u{bookPoint}
                                        </span>
                                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                          {underOdds > 0 ? `+${underOdds}` : underOdds}
                                        </span>
                                      </div>
                                    ) : (
                                      <span style={{ color: '#9CA3AF' }}>-</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          </React.Fragment>
                        );
                      } else {
                        // For non-yards markets, keep single row
                        return (
                          <tr key={`away-${player.playerId || player.name}-${player.point}`}>
                            <td style={{ textAlign: 'left', padding: '4px 8px' }}>
                              <span 
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  if (onPlayerClick) {
                                    onPlayerClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, safeGame.away, safeGame.home);
                                  }
                                }}
                              >
                                <span style={{ 
                                  color: shouldFadePlayer(player) ? '#9CA3AF' : 'inherit',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}>
                                  <img
                                    src={getTeamLogoUrl(safeGame.away)}
                                    alt={safeGame.away}
                                    style={{ width: '16px', height: '16px' }}
                                  />
                                  {formatPlayerName(player.name, isPlayerInLineup(player), safeGame.sport)}
                                </span>
                              </span>
                            </td>
                            {bookLogos.map(book => {
                              const odds = player[`${selectedMarket}_${book}`] ?? player[`${selectedMarket}_${book}_over`];
                              const bookCount = bookLogos.filter(b => (player[`${selectedMarket}_${b}`] ?? player[`${selectedMarket}_${b}_over`]) != null).length;

                              return (
                                <td
                                  key={`${selectedMarket}-${book}`}
                                  className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, book, player.point, 'over')}
                                  onClick={() => {
                                    if (odds && onOddsClick) {
                                      onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, odds, safeGame.away, safeGame.home, player.team, player.position, player.point, 'over');
                                    }
                                  }}
                                  style={{
                                    ...getCellStyle(
                                      odds,
                                      player[`${selectedMarket}_bestBookPrice`],
                                      player[`${selectedMarket}_bestBookDiff`],
                                      bookCount,
                                      player[`${selectedMarket}_${book}_direction`],
                                      book
                                    ),
                                    textAlign: 'center',
                                    padding: '4px',
                                    cursor: odds ? 'pointer' : 'default',
                                    opacity: shouldFadePlayer(player) ? 0.5 : 1
                                  }}
                                >
                                  {odds ? renderPrice(odds, player[`${selectedMarket}_${book}_direction`]) : <span style={{ color: '#9CA3AF' }}>-</span>}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      }
                    })}
                    
                    {/* Home Team Players */}
                    {homePlayers.map((player) => {
                      const isYardsMarket = ['player_pass_yds', 'player_rush_yds', 'player_reception_yds', 'player_rush_reception_yds'].includes(selectedMarket);
                      const isBasketballStatMarket = ['player_points', 'player_rebounds', 'player_assists', 'player_threes', 'player_points_rebounds_assists', 'player_points_rebounds', 'player_points_assists', 'player_rebounds_assists'].includes(selectedMarket);
                      const isMlbOuMarket = selectedMarket === 'pitcher_outs';

                      if (isYardsMarket || isBasketballStatMarket || isMlbOuMarket) {
                        // For yards markets and basketball stat markets, create two rows per player
                        return (
                          <React.Fragment key={`home-${player.playerId || player.name}-${player.point}`}>
                            {/* Over row */}
                            <tr>
                              <td rowSpan={2} style={{ textAlign: 'left', padding: '4px 8px' }}>
                                <span 
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    if (onPlayerClick) {
                                      onPlayerClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, safeGame.away, safeGame.home);
                                    }
                                  }}
                                >
                                  <span style={{ 
                                    color: shouldFadePlayer(player) ? '#9CA3AF' : 'inherit',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}>
                                    <img
                                      src={getTeamLogoUrl(safeGame.home)}
                                      alt={safeGame.home}
                                      style={{ width: '16px', height: '16px' }}
                                    />
                                    {formatPlayerName(player.name, isPlayerInLineup(player), safeGame.sport)}
                                  </span>
                                </span>
                              </td>
                              {bookLogos.map(book => {
                                const overOdds = player[`${selectedMarket}_${book}_over`];
                                const bookPoint = player[`${selectedMarket}_${book}_point`] || player.point;
                                
                                return (
                                  <td
                                    key={`${selectedMarket}-${book}-over`}
                                    className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, book, bookPoint, 'over')}
                                    onClick={() => {
                                      if (overOdds && onOddsClick) {
                                        onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, overOdds, safeGame.away, safeGame.home, player.team, player.position, bookPoint, 'over');
                                      }
                                    }}
                                    style={{
                                      textAlign: 'center',
                                      padding: '8px 4px',
                                      cursor: overOdds ? 'pointer' : 'default',
                                      opacity: shouldFadePlayer(player) ? 0.5 : 1
                                    }}
                                  >
                                    {overOdds !== null && overOdds !== undefined ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                          {overOdds > 0 ? `+${overOdds}` : overOdds}
                                        </span>
                                        <span style={{ fontSize: '0.65rem', color: '#6B7280' }}>
                                          o{bookPoint}
                                        </span>
                                      </div>
                                    ) : (
                                      <span style={{ color: '#9CA3AF' }}>-</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                            {/* Under row */}
                            <tr>
                              {bookLogos.map(book => {
                                const underOdds = player[`${selectedMarket}_${book}_under`];
                                const bookPoint = player[`${selectedMarket}_${book}_point`] || player.point;
                                
                                return (
                                  <td
                                    key={`${selectedMarket}-${book}-under`}
                                    className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, book, bookPoint, 'under')}
                                    onClick={() => {
                                      if (underOdds && onOddsClick) {
                                        onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, underOdds, safeGame.away, safeGame.home, player.team, player.position, bookPoint, 'under');
                                      }
                                    }}
                                    style={{
                                      textAlign: 'center',
                                      padding: '8px 4px',
                                      cursor: underOdds ? 'pointer' : 'default',
                                      opacity: shouldFadePlayer(player) ? 0.5 : 1
                                    }}
                                  >
                                    {underOdds !== null && underOdds !== undefined ? (
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '3px' }}>
                                        <span style={{ fontSize: '0.65rem', color: '#6B7280' }}>
                                          u{bookPoint}
                                        </span>
                                        <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                                          {underOdds > 0 ? `+${underOdds}` : underOdds}
                                        </span>
                                      </div>
                                    ) : (
                                      <span style={{ color: '#9CA3AF' }}>-</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          </React.Fragment>
                        );
                      } else {
                        // For non-yards markets, keep single row
                        return (
                          <tr key={`home-${player.playerId || player.name}-${player.point}`}>
                            <td style={{ textAlign: 'left', padding: '4px 8px' }}>
                              <span 
                                style={{ cursor: 'pointer' }}
                                onClick={() => {
                                  if (onPlayerClick) {
                                    onPlayerClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, safeGame.away, safeGame.home);
                                  }
                                }}
                              >
                                <span style={{ 
                                  color: shouldFadePlayer(player) ? '#9CA3AF' : 'inherit',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}>
                                  <img
                                    src={getTeamLogoUrl(safeGame.home)}
                                    alt={safeGame.home}
                                    style={{ width: '16px', height: '16px' }}
                                  />
                                  {formatPlayerName(player.name, isPlayerInLineup(player), safeGame.sport)}
                                </span>
                              </span>
                            </td>
                            {bookLogos.map(book => {
                              const odds = player[`${selectedMarket}_${book}`] ?? player[`${selectedMarket}_${book}_over`];
                              const bookCount = bookLogos.filter(b => (player[`${selectedMarket}_${b}`] ?? player[`${selectedMarket}_${b}_over`]) != null).length;

                              return (
                                <td
                                  key={`${selectedMarket}-${book}`}
                                  className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, book, player.point, 'over')}
                                  onClick={() => {
                                    if (odds && onOddsClick) {
                                      onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, odds, safeGame.away, safeGame.home, player.team, player.position, player.point, 'over');
                                    }
                                  }}
                                  style={{
                                    ...getCellStyle(
                                      odds,
                                      player[`${selectedMarket}_bestBookPrice`],
                                      player[`${selectedMarket}_bestBookDiff`],
                                      bookCount,
                                      player[`${selectedMarket}_${book}_direction`],
                                      book
                                    ),
                                    textAlign: 'center',
                                    padding: '4px',
                                    cursor: odds ? 'pointer' : 'default',
                                    opacity: shouldFadePlayer(player) ? 0.5 : 1
                                  }}
                                >
                                  {odds ? renderPrice(odds, player[`${selectedMarket}_${book}_direction`]) : <span style={{ color: '#9CA3AF' }}>-</span>}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      }
                    })}
                  </>
                );
              })()
              }
            </tbody>
          </Table>
        </div>
      )}

      {/* Desktop: Original single table for non-h2h, non-NFL markets */}
      {!isMobile && selectedMarket !== 'h2h' && (safeGame.sport !== 'nfl' || ['h2h', 'totals', 'spreads'].includes(selectedMarket)) && (
        <div className="d-flex" style={{ gap: '15px' }}>
          <div style={{ flex: '0 0 120px' }}>
            <div className="table-container">
              <Table bordered size="sm" className="text-center">
                <thead>
                  <tr><th className="bg-light" style={{ width: '120px' }}>{formatTime(gameTime, gameStatus)}</th></tr>
                  {safeGame.sport !== 'nfl' && (
                    <tr><th className="text-start" style={{ width: '120px' }}>Name</th></tr>
                  )}
                </thead>
                <tbody>
                  {sortedData.map((player, index) => {
                    const isFirstRowForPlayer = index === 0 || sortedData[index - 1].name !== player.name;
                    
                    // Show periodic headers for non-NFL sports
                    const shouldShowHeader = (index >= 20 && isFirstRowForPlayer && Math.floor(index / 15) > Math.floor((index - 1) / 15));
                    
                    return (
                      <React.Fragment key={player.tierKey || player.id}>
                        {/* Periodic headers for non-NFL sports */}
                        {shouldShowHeader && (
                          <tr>
                            <th className="text-start bg-light">Name</th>
                          </tr>
                        )}
                        <tr>
                          <td
                            className={(player.team || player.teamName || player.teamId) ? 'player-name-cell' : 'player-name-cell-no-logo'}
                            onContextMenu={(e) => {
                              if (onPlayerContextMenu) {
                                const teamAbbr = player.teamAbv || player.team || player.teamName || player.teamId || null;
                                onPlayerContextMenu(e, safeGame.gameIDmlb, player.playerId, player.name, teamAbbr);
                              }
                            }}
                            style={{
                              backgroundImage: (isFirstRowForPlayer && (player.team || player.teamName || player.teamId)) ? (() => {
                                const teamIdentifier = player.teamAbv || player.team || player.teamName || player.teamId;
                                if (teamIdentifier === 'WAS' || teamIdentifier === 'WSH' || player.name?.includes('Moore')) {
                                  console.log('DataTable Washington player logo:', { 
                                    playerName: player.name, 
                                    teamIdentifier,
                                    playerTeam: player.team,
                                    playerTeamId: player.teamId
                                  });
                                }
                                return `url(${getTeamLogoUrl(teamIdentifier)})`;
                              })() : 'none'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                              {isFirstRowForPlayer && (
                                <span 
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => {
                                    if (onPlayerClick) {
                                      onPlayerClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, safeGame.away, safeGame.home);
                                    }
                                  }}
                                >
                                  <span style={{ 
                                    color: shouldFadePlayer(player) ? '#9CA3AF' : 'inherit',
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    gap: '2px'
                                  }}>
                                    {(selectedMarket === 'h2h' || selectedMarket === 'spreads') && (player.team === 'away' || player.team === 'home') ? 
                                      <div style={{ display: 'flex', alignItems: 'center' }}>
                                        <img
                                          src={getTeamLogoUrl(player.team === 'away' ? safeGame.away : safeGame.home)}
                                          alt={player.team === 'away' ? safeGame.away : safeGame.home}
                                          style={{ width: '20px', height: '20px', marginRight: '8px' }}
                                        />
                                        <div style={{ display: 'flex', alignItems: 'baseline', gap: '4px' }}>
                                          <span>{player.team === 'away' ? (safeGame.awayNickname || safeGame.away) : (safeGame.homeNickname || safeGame.home)}</span>
                                          {(selectedMarket === 'spreads' || (selectedMarket === 'h2h' && player.betType && (player.betType === 'away' || player.betType === 'home') && player.point !== null && player.point !== undefined)) && (
                                            <span style={{ 
                                              color: '#9CA3AF', 
                                              fontSize: '0.85rem',
                                              fontWeight: 'normal'
                                            }}>
                                              {player.name}
                                            </span>
                                          )}
                                        </div>
                                      </div> : 
                                      (selectedMarket === 'totals' && player.betType) ?
                                        <span style={{ 
                                          fontSize: '0.95rem',
                                          fontWeight: 'bold',
                                          width: '100%',
                                          textAlign: 'right',
                                          display: 'block'
                                        }}>
                                          {player.betType.toUpperCase()}
                                        </span> :
                                      (selectedMarket === 'h2h' && player.playerId?.startsWith('totals_')) ?
                                        <span style={{ 
                                          color: '#9CA3AF', 
                                          fontSize: '0.85rem',
                                          fontWeight: 'normal',
                                          width: '100%',
                                          textAlign: 'right',
                                          display: 'block'
                                        }}>
                                          {player.name}
                                        </span> :
                                      (selectedMarket === 'h2h' && player.playerId?.startsWith('spreads_')) ?
                                        <div style={{ display: 'flex', alignItems: 'center' }}>
                                          <img
                                            src={getTeamLogoUrl(player.team === 'away' ? safeGame.away : safeGame.home)}
                                            alt={player.team === 'away' ? safeGame.away : safeGame.home}
                                            style={{ width: '20px', height: '20px', marginRight: '8px' }}
                                          />
                                          {player.team === 'away' ? safeGame.away : safeGame.home} {player.name}
                                        </div> :
                                        (() => {
                                          if (isTeamDefense(player.name)) {
                                            const teamAbbrev = getTeamFromDefenseName(player.name);
                                            console.log('D/ST Debug:', {
                                              playerName: player.name,
                                              extractedTeam: teamAbbrev,
                                              gameAway: safeGame.away,
                                              gameHome: safeGame.home,
                                              sport: safeGame.sport
                                            });
                                            if (teamAbbrev) {
                                              // Preserve the original format (D/ST or Defense)
                                              const isDefenseFormat = player.name.toLowerCase().includes('defense');
                                              const displayText = isDefenseFormat ? `${teamAbbrev} Defense` : `${teamAbbrev} D/ST`;
                                              
                                              return (
                                                <div style={{ display: 'flex', alignItems: 'center', marginLeft: '-8px' }}>
                                                  <img
                                                    src={getTeamLogoUrl(teamAbbrev)}
                                                    alt={teamAbbrev}
                                                    style={{ width: '20px', height: '20px', marginRight: '8px' }}
                                                  />
                                                  {displayText}
                                                </div>
                                              );
                                            }
                                          }
                                          return formatPlayerName(player.name, isPlayerInLineup(player), safeGame.sport);
                                        })()
                                    }
                                    {!selectedMarket?.startsWith('pitcher_') && getBattingOrderPosition(player) && (
                                      <sup style={{
                                        fontSize: '0.6em',
                                        color: '#B3B3B3',
                                        fontWeight: 'normal',
                                        lineHeight: '1',
                                        marginLeft: '2px'
                                      }}>
                                        {getBattingOrderPosition(player)}
                                      </sup>
                                    )}
                                    {(() => {
                                      if (safeGame.sport === 'wnba' && (player.positionCode || player.position?.abbreviation)) {
                                        return (
                                          <span style={{ 
                                            fontSize: '0.85em',
                                            color: '#9CA3AF',
                                            marginLeft: '4px',
                                            fontWeight: 'normal'
                                          }}>
                                            ({player.positionCode || player.position?.abbreviation})
                                          </span>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </span>
                                </span>
                              )}
                              {!isFirstRowForPlayer && <span></span>}
                              {player.point !== null && selectedMarket !== 'h2h' && !player.betType && (
                                <small style={{
                                  color: '#6B7280',
                                  fontSize: '0.75rem',
                                  marginLeft: '8px',
                                  flexShrink: 0
                                }}>
                                  {player.point === 0.5 && selectedMarket.includes('first_basket') ? 'Yes' : `Over ${player.point}`}
                                </small>
                              )}
                              {selectedMarket === 'pitcher_outs' && player.betType && player.point !== null && (
                                <small style={{
                                  color: '#6B7280',
                                  fontSize: '0.75rem',
                                  marginLeft: '8px',
                                  flexShrink: 0
                                }}>
                                  {player.betType === 'over' ? 'Over' : 'Under'} {player.point}
                                </small>
                              )}
                            </div>
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </div>

          <div style={{ flex: '1' }}>
            <div className="table-container">
              <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed', minWidth: `${bookLogos.length * 75}px` }}>
                <thead>
                  <tr><th colSpan={bookLogos.length} className="bg-light">{selectedMarketName}</th></tr>
                  <tr>
                    {bookLogos.map(book => (
                      <th key={`${selectedMarket}-${book}`} style={oddsCellStyle}>
                        <img 
                          src={getBookLogoUrl(book)} 
                          alt={`${book} logo`} 
                          title={book} 
                          style={{ height: '20px' }} 
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedData.map((player, index) => {
                    const count = bookLogos.filter(book =>
                      (player[`${selectedMarket}_${book}`] ?? player[`${selectedMarket}_${book}_over`]) !== null && (player[`${selectedMarket}_${book}`] ?? player[`${selectedMarket}_${book}_over`]) !== undefined
                    ).length;
                    const isFirstRowForPlayer = index === 0 || sortedData[index - 1].name !== player.name;

                    // For pitcher_outs Under rows the API's bestBookPrice/Diff are the
                    // over-side values, which would mis-highlight the under cells. Recompute
                    // the best under price from this row's per-book under values.
                    const isPitcherOutsUnderRow = selectedMarket === 'pitcher_outs' && player.betType === 'under';
                    let effectiveBestPrice = player[`${selectedMarket}_bestBookPrice`];
                    let effectiveBestDiff = player[`${selectedMarket}_bestBookDiff`];
                    let effectiveCount = count;
                    if (isPitcherOutsUnderRow) {
                      const underPrices = bookLogos
                        .map(book => player[`${selectedMarket}_${book}_under`])
                        .filter(v => v !== null && v !== undefined);
                      effectiveCount = underPrices.length;
                      if (underPrices.length > 0) {
                        const sorted = [...underPrices].sort((a, b) => b - a);
                        effectiveBestPrice = sorted[0];
                        if (sorted.length > 1 && sorted[0] !== sorted[1]) {
                          const best = sorted[0];
                          const second = sorted[1];
                          if (best > 0 && second < 0) {
                            const totalSpread = (best - 100) - (second - (-100));
                            effectiveBestDiff = Math.abs(totalSpread / second);
                          } else {
                            effectiveBestDiff = (best - second) / Math.abs(second);
                          }
                        } else {
                          effectiveBestDiff = 0;
                        }
                      } else {
                        effectiveBestPrice = null;
                        effectiveBestDiff = null;
                      }
                    }
                    
                    // Show header if we're past a multiple of 15 and this is the first row for a player
                    const shouldShowHeader = index >= 20 && isFirstRowForPlayer && 
                      Math.floor(index / 15) > Math.floor((index - 1) / 15);
                    
                    return (
                      <React.Fragment key={player.tierKey || player.id}>
                        {/* Repeat header row approximately every 15-20 rows, but only before a new player */}
                        {shouldShowHeader && (
                          <tr>
                            {bookLogos.map(book => (
                              <th key={`header-${selectedMarket}-${book}`} style={oddsCellStyle}>
                                <img 
                                  src={getBookLogoUrl(book)} 
                                  alt={`${book} logo`} 
                                  title={book} 
                                  style={{ height: '20px' }} 
                                />
                              </th>
                            ))}
                          </tr>
                        )}
                        <tr>
                          {bookLogos.map(book => (
                            <td
                              key={`${selectedMarket}-${book}`}
                              className={`odds-cell ${getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, book, player.point, player.betType || 'yes')}`}
                              style={{
                                ...getCellStyle(
                                  isPitcherOutsUnderRow
                                    ? player[`${selectedMarket}_${book}_under`]
                                    : (player[`${selectedMarket}_${book}`] ?? player[`${selectedMarket}_${book}_over`]),
                                  effectiveBestPrice,
                                  effectiveBestDiff,
                                  effectiveCount,
                                  player[`${selectedMarket}_${book}_direction`],
                                  book
                                ),
                                ...oddsCellStyle,
                                cursor: (isPitcherOutsUnderRow
                                  ? player[`${selectedMarket}_${book}_under`]
                                  : (player[`${selectedMarket}_${book}`] ?? player[`${selectedMarket}_${book}_over`])) ? 'pointer' : 'default',
                                opacity: player[`${selectedMarket}_${book}_isAvailable`] === false ? 0.25 : undefined
                              }}
                              title={getTooltipText(
                                isPitcherOutsUnderRow
                                  ? player[`${selectedMarket}_${book}_under`]
                                  : (player[`${selectedMarket}_${book}`] ?? player[`${selectedMarket}_${book}_over`]),
                                effectiveBestPrice,
                                effectiveBestDiff,
                                effectiveCount,
                                book
                              )}
                              onClick={() => {
                                // For pitcher_outs Under rows, click the under odds rather than over.
                                const cellOdds = (selectedMarket === 'pitcher_outs' && player.betType === 'under')
                                  ? player[`${selectedMarket}_${book}_under`]
                                  : (player[`${selectedMarket}_${book}`] ?? player[`${selectedMarket}_${book}_over`]);
                                if (cellOdds && onOddsClick) {
                                  onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, cellOdds, safeGame.away, safeGame.home, player.team, player.position, player.point, player.betType || 'over');
                                }
                              }}
                            >
                              {(() => {
                                // For h2h market with combined data, determine which market this row represents
                                if (selectedMarket === 'h2h') {
                                  if (player.playerId?.startsWith('totals_')) {
                                    const odds = player[`totals_${book}`];
                                    if (odds !== null && odds !== undefined) {
                                      return (
                                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                          <span style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>{player.betType === 'over' ? 'O' : 'U'} {player.point}</span>
                                          <span style={{ fontSize: '0.9rem' }}>{odds > 0 ? '+' : ''}{odds}</span>
                                        </div>
                                      );
                                    }
                                    return '\u00A0';
                                  } else if (player.playerId?.startsWith('spreads_')) {
                                    return renderPrice(player[`spreads_${book}`], player[`spreads_${book}_direction`]);
                                  } else {
                                    return renderPrice(player[`h2h_${book}`], player[`h2h_${book}_direction`]);
                                  }
                                } else if (selectedMarket === 'totals' && player.betType) {
                                  const odds = player[`${selectedMarket}_${book}`];
                                  if (odds !== null && odds !== undefined) {
                                    return (
                                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                        <span style={{ fontSize: '0.85rem', fontWeight: 'bold' }}>{player.betType === 'over' ? 'O' : 'U'} {player.point}</span>
                                        <span style={{ fontSize: '0.9rem' }}>{odds > 0 ? '+' : ''}{odds}</span>
                                      </div>
                                    );
                                  }
                                  return '\u00A0';
                                } else {
                                  // For all other markets, try bare key first, then _over key.
                                  // pitcher_outs Under rows pull from _under instead so the
                                  // duplicated Under row renders the under price in the same
                                  // single-line format as a normal odds cell.
                                  const odds = (selectedMarket === 'pitcher_outs' && player.betType === 'under')
                                    ? player[`${selectedMarket}_${book}_under`]
                                    : (player[`${selectedMarket}_${book}`] ?? player[`${selectedMarket}_${book}_over`]);
                                  return renderPrice(odds, player[`${selectedMarket}_${book}_direction`]);
                                }
                              })()}
                            </td>
                          ))}
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          </div>
        </div>
      )}

      {/* Mobile: Multiple tables for h2h market */}
      {isMobile && !isCollapsed && selectedMarket === 'h2h' && (h2hData.length > 0 || totalsData.length > 0 || spreadsData.length > 0) && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {/* Moneyline Table */}
          {h2hData.length > 0 && (
            <div>
              <div className="table-container" {...swipeHandlers}>
                <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed' }}>
                  <thead>
                    <tr><th colSpan={bookLogos.length + 1} className="bg-light">Moneyline</th></tr>
                    <tr>
                      <th style={{ width: '20%' }}></th>
                      {bookLogos.map(book => (
                        <th key={`h2h-${book}`} style={{ width: `${80/bookLogos.length}%`, textAlign: 'center' }}>
                          <img 
                            src={getBookLogoUrl(book)} 
                            alt={`${book} logo`} 
                            title={book} 
                            style={{ height: '16px' }} 
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {h2hData.map((player) => (
                      <tr key={player.tierKey || player.id}>
                        <td style={{ width: '20%', fontWeight: 'bold', fontSize: '0.7rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                            <img
                              src={getTeamLogoUrl(player.team === 'away' ? safeGame.away : safeGame.home)}
                              alt={player.team === 'away' ? safeGame.away : safeGame.home}
                              style={{ width: '16px', height: '16px' }}
                            />
                            <span>{player.team === 'away' ? (safeGame.awayNickname || safeGame.away) : (safeGame.homeNickname || safeGame.home)}</span>
                          </div>
                        </td>
                        {bookLogos.map(book => {
                          const count = bookLogos.filter(b => 
                            player[`h2h_${b}`] !== null && player[`h2h_${b}`] !== undefined
                          ).length;
                          
                          return (
                            <td
                              key={`h2h-${book}`}
                              className={`odds-cell ${getCellClassName(safeGame.gameIDmlb, player.playerId, 'h2h', book, null, 'moneyline')}`}
                              style={{
                                width: `${80/bookLogos.length}%`,
                                textAlign: 'center',
                                fontSize: '0.8rem',
                                ...getCellStyle(
                                  player[`h2h_${book}`],
                                  player[`h2h_bestBookPrice`],
                                  player[`h2h_bestBookDiff`],
                                  count,
                                  player[`h2h_${book}_direction`],
                                  book
                                ),
                                cursor: player[`h2h_${book}`] ? 'pointer' : 'default',
                                opacity: player[`h2h_${book}_isAvailable`] === false ? 0.25 : undefined
                              }}
                              onClick={() => {
                                if (onOddsClick && player[`h2h_${book}`]) {
                                  onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, 'h2h', book, player[`h2h_${book}`], safeGame.away, safeGame.home, player.team || player.name, null, null, 'moneyline', (player.team || player.name) === safeGame.home ? safeGame.homeTeamId : safeGame.awayTeamId);
                                }
                              }}
                            >
                              {renderPrice(player[`h2h_${book}`], player[`h2h_${book}_direction`])}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}

          {/* Totals Table */}
          {totalsData.length > 0 && (
            <div>
              <div className="table-container" {...swipeHandlers}>
                <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed' }}>
                  <thead>
                    <tr><th colSpan={bookLogos.length + 1} className="bg-light">Totals</th></tr>
                    <tr>
                      <th style={{ width: '20%' }}></th>
                      {bookLogos.map(book => (
                        <th key={`totals-${book}`} style={{ width: `${80/bookLogos.length}%`, textAlign: 'center' }}>
                          <img 
                            src={getBookLogoUrl(book)} 
                            alt={`${book} logo`} 
                            title={book} 
                            style={{ height: '16px' }} 
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Over Row */}
                    <tr>
                      <td style={{ width: '20%', fontWeight: 'bold', fontSize: '0.7rem' }}>Over</td>
                      {bookLogos.map(book => {
                        // Find all over entries and group them by point value
                        const overEntries = totalsData.filter(p => p.betType === 'over');
                        let cellContent = [];
                        
                        overEntries.forEach(player => {
                          const odds = player[`totals_${book}`];
                          if (odds !== null && odds !== undefined) {
                            cellContent.push({
                              point: player.point,
                              odds: odds,
                              player: player
                            });
                          }
                        });
                        
                        // Sort by point value
                        cellContent.sort((a, b) => a.point - b.point);
                        
                        return (
                          <td
                            key={`totals-over-${book}`}
                            className="odds-cell"
                            style={{
                              width: `${80/bookLogos.length}%`,
                              textAlign: 'center',
                              fontSize: '0.6rem'
                            }}
                          >
                            {cellContent.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                {cellContent.map(({ point, odds, player }) => (
                                  <div 
                                    key={`over-${point}`}
                                    style={{ 
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      alignItems: 'center',
                                      borderBottom: cellContent.length > 1 ? '1px solid #dee2e6' : 'none',
                                      paddingBottom: cellContent.length > 1 ? '1px' : '0'
                                    }}
                                  >
                                    <span style={{ fontSize: '0.6rem', fontWeight: 'bold', color: '#9CA3AF', marginBottom: '2px' }}>O {point}</span>
                                    <span style={{ fontSize: '0.8rem' }}>{odds > 0 ? '+' : ''}{odds}</span>
                                  </div>
                                ))}
                              </div>
                            ) : '\u00A0'}
                          </td>
                        );
                      })}
                    </tr>
                    
                    {/* Under Row */}
                    <tr>
                      <td style={{ width: '20%', fontWeight: 'bold', fontSize: '0.7rem' }}>Under</td>
                      {bookLogos.map(book => {
                        // Find all under entries and group them by point value
                        const underEntries = totalsData.filter(p => p.betType === 'under');
                        let cellContent = [];
                        
                        underEntries.forEach(player => {
                          const odds = player[`totals_${book}`];
                          if (odds !== null && odds !== undefined) {
                            cellContent.push({
                              point: player.point,
                              odds: odds,
                              player: player
                            });
                          }
                        });
                        
                        // Sort by point value
                        cellContent.sort((a, b) => a.point - b.point);
                        
                        return (
                          <td
                            key={`totals-under-${book}`}
                            className="odds-cell"
                            style={{
                              width: `${80/bookLogos.length}%`,
                              textAlign: 'center',
                              fontSize: '0.6rem'
                            }}
                          >
                            {cellContent.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                {cellContent.map(({ point, odds, player }) => (
                                  <div 
                                    key={`under-${point}`}
                                    style={{ 
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      alignItems: 'center',
                                      borderBottom: cellContent.length > 1 ? '1px solid #dee2e6' : 'none',
                                      paddingBottom: cellContent.length > 1 ? '1px' : '0'
                                    }}
                                  >
                                    <span style={{ fontSize: '0.6rem', fontWeight: 'bold', color: '#9CA3AF', marginBottom: '2px' }}>U {point}</span>
                                    <span style={{ fontSize: '0.8rem' }}>{odds > 0 ? '+' : ''}{odds}</span>
                                  </div>
                                ))}
                              </div>
                            ) : '\u00A0'}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>
          )}

          {/* Spreads Table */}
          {spreadsData.length > 0 && (
            <div>
              <div className="table-container" {...swipeHandlers}>
                <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed' }}>
                  <thead>
                    <tr><th colSpan={bookLogos.length + 1} className="bg-light">Spreads</th></tr>
                    <tr>
                      <th style={{ width: '25%' }}></th>
                      {bookLogos.map(book => (
                        <th key={`spreads-${book}`} style={{ width: `${75/bookLogos.length}%`, textAlign: 'center' }}>
                          <img 
                            src={getBookLogoUrl(book)} 
                            alt={`${book} logo`} 
                            title={book} 
                            style={{ height: '16px' }} 
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* Away Team Row */}
                    <tr>
                      <td style={{ width: '25%', fontWeight: 'bold', fontSize: '0.65rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                          <img
                            src={getTeamLogoUrl(safeGame.away)}
                            alt={safeGame.away}
                            style={{ width: '12px', height: '12px' }}
                          />
                          <span>{safeGame.away}</span>
                        </div>
                      </td>
                      {bookLogos.map(book => {
                        // Find all away team entries and group them by point value
                        const awayEntries = spreadsData.filter(p => p.team === 'away');
                        let cellContent = [];
                        
                        awayEntries.forEach(player => {
                          const odds = player[`spreads_${book}`];
                          if (odds !== null && odds !== undefined) {
                            cellContent.push({
                              point: player.point,
                              odds: odds,
                              player: player
                            });
                          }
                        });
                        
                        // Sort by point value
                        cellContent.sort((a, b) => a.point - b.point);
                        
                        return (
                          <td
                            key={`spreads-away-${book}`}
                            className="odds-cell"
                            style={{
                              width: `${75/bookLogos.length}%`,
                              textAlign: 'center',
                              fontSize: '0.55rem'
                            }}
                          >
                            {cellContent.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                {cellContent.map(({ point, odds, player }) => (
                                  <div 
                                    key={`away-${point}`}
                                    style={{ 
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      alignItems: 'center',
                                      borderBottom: cellContent.length > 1 ? '1px solid #dee2e6' : 'none',
                                      paddingBottom: cellContent.length > 1 ? '1px' : '0'
                                    }}
                                  >
                                    <span style={{ fontSize: '0.55rem', fontWeight: 'bold', color: '#9CA3AF', marginBottom: '2px' }}>{point > 0 ? `+${point}` : `${point}`}</span>
                                    <span style={{ fontSize: '0.75rem' }}>{odds > 0 ? '+' : ''}{odds}</span>
                                  </div>
                                ))}
                              </div>
                            ) : '\u00A0'}
                          </td>
                        );
                      })}
                    </tr>
                    
                    {/* Home Team Row */}
                    <tr>
                      <td style={{ width: '25%', fontWeight: 'bold', fontSize: '0.65rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px' }}>
                          <img
                            src={getTeamLogoUrl(safeGame.home)}
                            alt={safeGame.home}
                            style={{ width: '12px', height: '12px' }}
                          />
                          <span>{safeGame.home}</span>
                        </div>
                      </td>
                      {bookLogos.map(book => {
                        // Find all home team entries and group them by point value
                        const homeEntries = spreadsData.filter(p => p.team === 'home');
                        let cellContent = [];
                        
                        homeEntries.forEach(player => {
                          const odds = player[`spreads_${book}`];
                          if (odds !== null && odds !== undefined) {
                            cellContent.push({
                              point: player.point,
                              odds: odds,
                              player: player
                            });
                          }
                        });
                        
                        // Sort by point value
                        cellContent.sort((a, b) => a.point - b.point);
                        
                        return (
                          <td
                            key={`spreads-home-${book}`}
                            className="odds-cell"
                            style={{
                              width: `${75/bookLogos.length}%`,
                              textAlign: 'center',
                              fontSize: '0.55rem'
                            }}
                          >
                            {cellContent.length > 0 ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                {cellContent.map(({ point, odds, player }) => (
                                  <div 
                                    key={`home-${point}`}
                                    style={{ 
                                      display: 'flex', 
                                      flexDirection: 'column', 
                                      alignItems: 'center',
                                      borderBottom: cellContent.length > 1 ? '1px solid #dee2e6' : 'none',
                                      paddingBottom: cellContent.length > 1 ? '1px' : '0'
                                    }}
                                  >
                                    <span style={{ fontSize: '0.55rem', fontWeight: 'bold', color: '#9CA3AF', marginBottom: '2px' }}>{point > 0 ? `+${point}` : `${point}`}</span>
                                    <span style={{ fontSize: '0.75rem' }}>{odds > 0 ? '+' : ''}{odds}</span>
                                  </div>
                                ))}
                              </div>
                            ) : '\u00A0'}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* NFL mobile section removed - handled by NFLDataTable */}
      {false && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
          {/* Away Team Table */}
          {sortedData.filter(p => p.team === safeGame.away).length > 0 && (
            <div 
              className="table-container"
              {...swipeHandlers}
              style={{
                transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.2s ease',
                transform: isTransitioning ? 'scale(0.98)' : 'scale(1)',
                opacity: isTransitioning ? 0.8 : 1
              }}
            >
              <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th className="bg-primary text-white" style={{ width: `${mobileNameWidth}%` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem' }}>
                        <img src={getTeamLogoUrl(safeGame.away)} alt={safeGame.away} style={{ width: '14px', height: '14px' }} />
                        {safeGame.awayNickname || safeGame.away}
                      </div>
                    </th>
                    <th colSpan={bookLogos.length} className="bg-primary text-white" style={{ width: `${mobileBookWidth * bookLogos.length}%` }}>
                      {selectedMarketName}
                    </th>
                  </tr>
                  <tr>
                    <th className="text-start" style={{ width: `${mobileNameWidth}%` }}>Name</th>
                    {bookLogos.map(book => (
                      <th key={`${selectedMarket}-${book}`} style={{ width: `${mobileBookWidth}%`, textAlign: 'center' }}>
                        <img 
                          src={getBookLogoUrl(book)} 
                          alt={`${book} logo`} 
                          title={book} 
                          style={{ height: '16px' }} 
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedData.filter(p => p.team === safeGame.away).map((player, index) => {
                    const isFirstRowForPlayer = index === 0 || sortedData.filter(p => p.team === safeGame.away)[index - 1].name !== player.name;
                    
                    return (
                      <React.Fragment key={player.tierKey || player.id}>
                        <tr>
                          <td
                            className={(player.team || player.teamName) ? 'player-name-cell' : 'player-name-cell-no-logo'}
                            onContextMenu={(e) => {
                              if (onPlayerContextMenu) {
                                const teamAbbr = player.teamAbv || player.team || player.teamName || player.teamId || null;
                                onPlayerContextMenu(e, safeGame.gameIDmlb, player.playerId, player.name, teamAbbr);
                              }
                            }}
                            style={{
                              backgroundImage: (isFirstRowForPlayer && (player.team || player.teamName)) ? `url(${getTeamLogoUrl(player.teamAbv || player.team || player.teamName || player.teamId)})` : 'none',
                              width: `${mobileNameWidth}%`,
                              fontSize: '0.8rem'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                              {isFirstRowForPlayer && (
                                <span
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => onPlayerClick && onPlayerClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, safeGame.away, safeGame.home)}
                                >
                                  <span style={{ 
                                    color: shouldFadePlayer(player) ? '#9CA3AF' : 'inherit',
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    gap: '2px'
                                  }}>
                                    {(() => {
                                      if (isTeamDefense(player.name)) {
                                        const teamAbbrev = getTeamFromDefenseName(player.name);
                                        if (teamAbbrev) {
                                          const isDefenseFormat = player.name.toLowerCase().includes('defense');
                                          const displayText = isDefenseFormat ? `${teamAbbrev} Defense` : `${teamAbbrev} D/ST`;
                                          return (
                                            <div style={{ display: 'flex', alignItems: 'center', marginLeft: '-8px' }}>
                                              <img
                                                src={getTeamLogoUrl(teamAbbrev)}
                                                alt={teamAbbrev}
                                                style={{ width: '16px', height: '16px', marginRight: '6px' }}
                                              />
                                              {displayText}
                                            </div>
                                          );
                                        }
                                      }
                                      return formatPlayerName(player.name, isPlayerInLineup(player), safeGame.sport);
                                    })()}
                                  </span>
                                </span>
                              )}
                              {!isFirstRowForPlayer && <span></span>}
                              {player.point !== null && selectedMarket !== 'h2h' && !player.betType && (
                                <small style={{ 
                                  color: '#6B7280', 
                                  fontSize: '0.65rem',
                                  marginLeft: '4px',
                                  flexShrink: 0
                                }}>
                                  {player.point === 0.5 && selectedMarket.includes('first_basket') ? 'Yes' : player.point}
                                </small>
                              )}
                            </div>
                          </td>
                          {bookLogos.map(book => {
                            const odds = player[`${selectedMarket}_${book}`] ?? player[`${selectedMarket}_${book}_over`];
                            const hasNoOdds = odds === null || odds === undefined;
                            const tierValue = player.tierValue || null;

                            return (
                              <td
                                key={`${selectedMarket}-${book}`}
                                className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, book, null, 'yes')}
                                onClick={() => {
                                  if (odds && onOddsClick) {
                                    onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, odds, safeGame.away, safeGame.home, player.team, player.position, player.point, 'over');
                                  }
                                }}
                                style={{
                                  ...getCellStyle(
                                    odds,
                                    player[`${selectedMarket}_bestBookPrice`],
                                    player[`${selectedMarket}_bestBookDiff`],
                                    bookLogos.filter(b => (player[`${selectedMarket}_${b}`] ?? player[`${selectedMarket}_${b}_over`]) !== null && (player[`${selectedMarket}_${b}`] ?? player[`${selectedMarket}_${b}_over`]) !== undefined).length,
                                    player[`${selectedMarket}_${book}_direction`],
                                    book
                                  ),
                                  cursor: hasNoOdds ? 'default' : 'pointer',
                                  width: `${mobileBookWidth}%`,
                                  fontSize: '0.75rem',
                                  padding: '4px 2px',
                                  position: 'relative',
                                  opacity: shouldFadePlayer(player) ? 0.5 : 1
                                }}
                                title={getTooltipText(odds, player[`${selectedMarket}_bestBookPrice`], player[`${selectedMarket}_bestBookDiff`], bookLogos.filter(b => (player[`${selectedMarket}_${b}`] ?? player[`${selectedMarket}_${b}_over`]) !== null && (player[`${selectedMarket}_${b}`] ?? player[`${selectedMarket}_${b}_over`]) !== undefined).length, book)}
                              >
                                {renderPrice(odds, player[`${selectedMarket}_${book}_direction`])}
                              </td>
                            );
                          })}
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}

          {/* Home Team Table */}
          {sortedData.filter(p => p.team === safeGame.home).length > 0 && (
            <div 
              className="table-container"
              {...swipeHandlers}
              style={{
                transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.2s ease',
                transform: isTransitioning ? 'scale(0.98)' : 'scale(1)',
                opacity: isTransitioning ? 0.8 : 1
              }}
            >
              <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th className="bg-primary text-white" style={{ width: `${mobileNameWidth}%` }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.75rem' }}>
                        <img src={getTeamLogoUrl(safeGame.home)} alt={safeGame.home} style={{ width: '14px', height: '14px' }} />
                        {safeGame.homeNickname || safeGame.home}
                      </div>
                    </th>
                    <th colSpan={bookLogos.length} className="bg-primary text-white" style={{ width: `${mobileBookWidth * bookLogos.length}%` }}>
                      {selectedMarketName}
                    </th>
                  </tr>
                  <tr>
                    <th className="text-start" style={{ width: `${mobileNameWidth}%` }}>Name</th>
                    {bookLogos.map(book => (
                      <th key={`${selectedMarket}-${book}`} style={{ width: `${mobileBookWidth}%`, textAlign: 'center' }}>
                        <img 
                          src={getBookLogoUrl(book)} 
                          alt={`${book} logo`} 
                          title={book} 
                          style={{ height: '16px' }} 
                        />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedData.filter(p => p.team === safeGame.home).map((player, index) => {
                    const isFirstRowForPlayer = index === 0 || sortedData.filter(p => p.team === safeGame.home)[index - 1].name !== player.name;
                    
                    return (
                      <React.Fragment key={player.tierKey || player.id}>
                        <tr>
                          <td
                            className={(player.team || player.teamName) ? 'player-name-cell' : 'player-name-cell-no-logo'}
                            onContextMenu={(e) => {
                              if (onPlayerContextMenu) {
                                const teamAbbr = player.teamAbv || player.team || player.teamName || player.teamId || null;
                                onPlayerContextMenu(e, safeGame.gameIDmlb, player.playerId, player.name, teamAbbr);
                              }
                            }}
                            style={{
                              backgroundImage: (isFirstRowForPlayer && (player.team || player.teamName)) ? `url(${getTeamLogoUrl(player.teamAbv || player.team || player.teamName || player.teamId)})` : 'none',
                              width: `${mobileNameWidth}%`,
                              fontSize: '0.8rem'
                            }}
                          >
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                              {isFirstRowForPlayer && (
                                <span
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => onPlayerClick && onPlayerClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, safeGame.away, safeGame.home)}
                                >
                                  <span style={{ 
                                    color: shouldFadePlayer(player) ? '#9CA3AF' : 'inherit',
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    gap: '2px'
                                  }}>
                                    {(() => {
                                      if (isTeamDefense(player.name)) {
                                        const teamAbbrev = getTeamFromDefenseName(player.name);
                                        if (teamAbbrev) {
                                          const isDefenseFormat = player.name.toLowerCase().includes('defense');
                                          const displayText = isDefenseFormat ? `${teamAbbrev} Defense` : `${teamAbbrev} D/ST`;
                                          return (
                                            <div style={{ display: 'flex', alignItems: 'center', marginLeft: '-8px' }}>
                                              <img
                                                src={getTeamLogoUrl(teamAbbrev)}
                                                alt={teamAbbrev}
                                                style={{ width: '16px', height: '16px', marginRight: '6px' }}
                                              />
                                              {displayText}
                                            </div>
                                          );
                                        }
                                      }
                                      return formatPlayerName(player.name, isPlayerInLineup(player), safeGame.sport);
                                    })()}
                                  </span>
                                </span>
                              )}
                              {!isFirstRowForPlayer && <span></span>}
                              {player.point !== null && selectedMarket !== 'h2h' && !player.betType && (
                                <small style={{ 
                                  color: '#6B7280', 
                                  fontSize: '0.65rem',
                                  marginLeft: '4px',
                                  flexShrink: 0
                                }}>
                                  {player.point === 0.5 && selectedMarket.includes('first_basket') ? 'Yes' : player.point}
                                </small>
                              )}
                            </div>
                          </td>
                          {bookLogos.map(book => {
                            const odds = player[`${selectedMarket}_${book}`] ?? player[`${selectedMarket}_${book}_over`];
                            const hasNoOdds = odds === null || odds === undefined;
                            const tierValue = player.tierValue || null;

                            return (
                              <td
                                key={`${selectedMarket}-${book}`}
                                className={getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, book, null, 'yes')}
                                onClick={() => {
                                  if (odds && onOddsClick) {
                                    onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, odds, safeGame.away, safeGame.home, player.team, player.position, player.point, 'over');
                                  }
                                }}
                                style={{
                                  ...getCellStyle(
                                    odds,
                                    player[`${selectedMarket}_bestBookPrice`],
                                    player[`${selectedMarket}_bestBookDiff`],
                                    bookLogos.filter(b => (player[`${selectedMarket}_${b}`] ?? player[`${selectedMarket}_${b}_over`]) !== null && (player[`${selectedMarket}_${b}`] ?? player[`${selectedMarket}_${b}_over`]) !== undefined).length,
                                    player[`${selectedMarket}_${book}_direction`],
                                    book
                                  ),
                                  cursor: hasNoOdds ? 'default' : 'pointer',
                                  width: `${mobileBookWidth}%`,
                                  fontSize: '0.75rem',
                                  padding: '4px 2px',
                                  position: 'relative',
                                  opacity: shouldFadePlayer(player) ? 0.5 : 1
                                }}
                                title={getTooltipText(odds, player[`${selectedMarket}_bestBookPrice`], player[`${selectedMarket}_bestBookDiff`], bookLogos.filter(b => (player[`${selectedMarket}_${b}`] ?? player[`${selectedMarket}_${b}_over`]) !== null && (player[`${selectedMarket}_${b}`] ?? player[`${selectedMarket}_${b}_over`]) !== undefined).length, book)}
                              >
                                {renderPrice(odds, player[`${selectedMarket}_${book}_direction`])}
                              </td>
                            );
                          })}
                        </tr>
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </Table>
            </div>
          )}
        </div>
      )}

      {/* Mobile: Combined table for non-NFL, non-h2h markets */}
      {isMobile && !isCollapsed && selectedMarket !== 'h2h' && (safeGame.sport !== 'nfl' || ['h2h', 'totals', 'spreads'].includes(selectedMarket)) && (
        <div 
          className="table-container" 
          {...swipeHandlers}
          style={{
            transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.2s ease',
            transform: isTransitioning ? 'scale(0.98)' : 'scale(1)',
            opacity: isTransitioning ? 0.8 : 1
          }}
        >
          <Table bordered size="sm" className="text-center" style={{ tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th className="bg-light" style={{ width: `${mobileNameWidth}%` }}>{formatTime(gameTime, gameStatus)}</th>
                <th colSpan={bookLogos.length} className="bg-light" style={{ width: `${mobileBookWidth * bookLogos.length}%` }}>
                  {selectedMarketName}
                </th>
              </tr>
              {safeGame.sport !== 'nfl' && (
                <tr>
                  <th className="text-start" style={{ width: `${mobileNameWidth}%` }}>Name</th>
                  {bookLogos.map(book => (
                    <th key={`${selectedMarket}-${book}`} style={{ width: `${mobileBookWidth}%`, textAlign: 'center' }}>
                      <img 
                        src={getBookLogoUrl(book)} 
                        alt={`${book} logo`} 
                        title={book} 
                        style={{ height: '16px' }} 
                      />
                    </th>
                  ))}
                </tr>
              )}
            </thead>
            <tbody>
              {sortedData.map((player, index) => {
                const count = bookLogos.filter(book =>
                  (player[`${selectedMarket}_${book}`] ?? player[`${selectedMarket}_${book}_over`]) !== null && (player[`${selectedMarket}_${book}`] ?? player[`${selectedMarket}_${book}_over`]) !== undefined
                ).length;
                const isFirstRowForPlayer = index === 0 || sortedData[index - 1].name !== player.name;

                // Show periodic headers for non-NFL sports
                const shouldShowHeader = (index >= 20 && isFirstRowForPlayer && Math.floor(index / 15) > Math.floor((index - 1) / 15));

                return (
                  <React.Fragment key={player.tierKey || player.id}>
                    {/* Periodic headers for non-NFL sports */}
                    {shouldShowHeader && (
                      <tr>
                        <th className="text-start bg-light" style={{ width: `${mobileNameWidth}%` }}>Name</th>
                        {bookLogos.map(book => (
                          <th key={`header-${selectedMarket}-${book}`} style={{ width: `${mobileBookWidth}%`, textAlign: 'center' }}>
                            <img
                              src={getBookLogoUrl(book)}
                              alt={`${book} logo`}
                              title={book}
                              style={{ height: '16px' }}
                            />
                          </th>
                        ))}
                      </tr>
                    )}
                    <tr>
                      <td
                        className={(player.team || player.teamName) ? 'player-name-cell' : 'player-name-cell-no-logo'}
                        onContextMenu={(e) => {
                          if (onPlayerContextMenu) {
                            const teamAbbr = player.teamAbv || player.team || player.teamName || player.teamId || null;
                            onPlayerContextMenu(e, safeGame.gameIDmlb, player.playerId, player.name, teamAbbr);
                          }
                        }}
                        style={{
                          backgroundImage: (isFirstRowForPlayer && (player.team || player.teamName)) ? `url(${getTeamLogoUrl(player.teamAbv || player.team || player.teamName || player.teamId)})` : 'none',
                          width: `${mobileNameWidth}%`,
                          fontSize: '0.8rem'
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                          {isFirstRowForPlayer && (
                            <span
                              style={{ cursor: 'pointer' }}
                              onClick={() => onPlayerClick && onPlayerClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, safeGame.away, safeGame.home)}
                            >
                              <span style={{ 
                                color: shouldFadePlayer(player) ? '#9CA3AF' : 'inherit',
                                display: 'flex',
                                alignItems: 'baseline',
                                gap: '2px'
                              }}>
                                {(selectedMarket === 'h2h' || selectedMarket === 'spreads') && (player.team === 'away' || player.team === 'home') ? 
                                  <div style={{ display: 'flex', alignItems: 'center' }}>
                                    <img
                                      src={getTeamLogoUrl(player.team === 'away' ? safeGame.away : safeGame.home)}
                                      alt={player.team === 'away' ? safeGame.away : safeGame.home}
                                      style={{ width: '16px', height: '16px', marginRight: '6px' }}
                                    />
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '3px' }}>
                                      <span>{player.team === 'away' ? (safeGame.awayNickname || safeGame.away) : (safeGame.homeNickname || safeGame.home)}</span>
                                      {(selectedMarket === 'spreads' || (selectedMarket === 'h2h' && player.betType && (player.betType === 'away' || player.betType === 'home') && player.point !== null && player.point !== undefined)) && (
                                        <span style={{ 
                                          color: '#9CA3AF', 
                                          fontSize: '0.75rem',
                                          fontWeight: 'normal'
                                        }}>
                                          {player.name}
                                        </span>
                                      )}
                                    </div>
                                  </div> : 
                                  (selectedMarket === 'totals' && player.betType) ?
                                    <span style={{ 
                                      fontSize: '0.85rem',
                                      fontWeight: 'bold',
                                      width: '100%',
                                      textAlign: 'right',
                                      display: 'block'
                                    }}>
                                      {player.betType.toUpperCase()}
                                    </span> :
                                  (selectedMarket === 'h2h' && player.playerId?.startsWith('totals_')) ?
                                    <span style={{ 
                                      color: '#9CA3AF', 
                                      fontSize: '0.75rem',
                                      fontWeight: 'normal',
                                      width: '100%',
                                      textAlign: 'right',
                                      display: 'block'
                                    }}>
                                      {player.name}
                                    </span> :
                                  (selectedMarket === 'h2h' && player.playerId?.startsWith('spreads_')) ?
                                    <div style={{ display: 'flex', alignItems: 'center' }}>
                                      <img
                                        src={getTeamLogoUrl(player.team === 'away' ? safeGame.away : safeGame.home)}
                                        alt={player.team === 'away' ? safeGame.away : safeGame.home}
                                        style={{ width: '16px', height: '16px', marginRight: '6px' }}
                                      />
                                      {player.team === 'away' ? safeGame.away : safeGame.home} {player.name}
                                    </div> :
                                    (() => {
                                      if (isTeamDefense(player.name)) {
                                        const teamAbbrev = getTeamFromDefenseName(player.name);
                                        if (teamAbbrev) {
                                          // Preserve the original format (D/ST or Defense)
                                          const isDefenseFormat = player.name.toLowerCase().includes('defense');
                                          const displayText = isDefenseFormat ? `${teamAbbrev} Defense` : `${teamAbbrev} D/ST`;
                                          
                                          return (
                                            <div style={{ display: 'flex', alignItems: 'center', marginLeft: '-8px' }}>
                                              <img
                                                src={getTeamLogoUrl(teamAbbrev)}
                                                alt={teamAbbrev}
                                                style={{ width: '16px', height: '16px', marginRight: '6px' }}
                                              />
                                              {displayText}
                                            </div>
                                          );
                                        }
                                      }
                                      return formatPlayerName(player.name, isPlayerInLineup(player), safeGame.sport);
                                    })()
                                }
                                {!selectedMarket?.startsWith('pitcher_') && getBattingOrderPosition(player) && (
                                  <sup style={{
                                    fontSize: '0.55em',
                                    color: '#B3B3B3',
                                    fontWeight: 'normal',
                                    lineHeight: '1',
                                    marginLeft: '2px'
                                  }}>
                                    {getBattingOrderPosition(player)}
                                  </sup>
                                )}
                                {safeGame.sport === 'wnba' && (player.positionCode || player.position?.abbreviation) && (
                                  <span style={{ 
                                    fontSize: '0.75em',
                                    color: '#9CA3AF',
                                    marginLeft: '2px',
                                    fontWeight: 'normal'
                                  }}>
                                    ({player.positionCode || player.position?.abbreviation})
                                  </span>
                                )}
                              </span>
                            </span>
                          )}
                          {!isFirstRowForPlayer && <span></span>}
                          {player.point !== null && selectedMarket !== 'h2h' && !player.betType && (
                            <small style={{ 
                              color: '#6B7280', 
                              fontSize: '0.65rem',
                              marginLeft: '4px',
                              flexShrink: 0
                            }}>
                              {player.point === 0.5 && selectedMarket.includes('first_basket') ? 'Yes' : player.point}
                            </small>
                          )}
                        </div>
                      </td>
                      {bookLogos.map(book => {
                        const cellOdds = player[`${selectedMarket}_${book}`] ?? player[`${selectedMarket}_${book}_over`];
                        return (
                        <td
                          key={`${selectedMarket}-${book}`}
                          className={`odds-cell ${getCellClassName(safeGame.gameIDmlb, player.playerId, selectedMarket, book, player.point, player.betType || 'yes')}`}
                          style={{
                            ...getCellStyle(
                              cellOdds,
                              player[`${selectedMarket}_bestBookPrice`],
                              player[`${selectedMarket}_bestBookDiff`],
                              count,
                              player[`${selectedMarket}_${book}_direction`],
                              book
                            ),
                            width: `${mobileBookWidth}%`,
                            textAlign: 'center',
                            fontSize: '0.75rem',
                            paddingLeft: '3px',
                            paddingRight: '3px',
                            cursor: cellOdds ? 'pointer' : 'default',
                            opacity: player[`${selectedMarket}_${book}_isAvailable`] === false ? 0.25 : undefined
                          }}
                          title={getTooltipText(
                            cellOdds,
                            player[`${selectedMarket}_bestBookPrice`],
                            player[`${selectedMarket}_bestBookDiff`],
                            count,
                            book
                          )}
                          onClick={() => {
                            if (cellOdds && onOddsClick) {
                              onOddsClick(safeGame.gameIDmlb, player.playerId, player.name, selectedMarket, book, cellOdds, safeGame.away, safeGame.home, player.team, player.position, player.point, player.betType || 'yes');
                            }
                          }}
                        >
                          {(() => {
                            // For h2h market with combined data, determine which market this row represents
                            if (selectedMarket === 'h2h') {
                              if (player.playerId?.startsWith('totals_')) {
                                const odds = player[`totals_${book}`];
                                if (odds !== null && odds !== undefined) {
                                  return (
                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                                      <span style={{ fontSize: '0.65rem', fontWeight: 'bold' }}>{player.betType === 'over' ? 'O' : 'U'} {player.point}</span>
                                      <span style={{ fontSize: '0.7rem' }}>{odds > 0 ? '+' : ''}{odds}</span>
                                    </div>
                                  );
                                }
                                return '\u00A0';
                              } else if (player.playerId?.startsWith('spreads_')) {
                                return renderPrice(player[`spreads_${book}`], player[`spreads_${book}_direction`]);
                              } else {
                                return renderPrice(player[`h2h_${book}`], player[`h2h_${book}_direction`]);
                              }
                            } else if (selectedMarket === 'totals' && player.betType) {
                              const odds = player[`${selectedMarket}_${book}`];
                              if (odds !== null && odds !== undefined) {
                                return (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1px' }}>
                                    <span style={{ fontSize: '0.65rem', fontWeight: 'bold' }}>{player.betType === 'over' ? 'O' : 'U'} {player.point}</span>
                                    <span style={{ fontSize: '0.7rem' }}>{odds > 0 ? '+' : ''}{odds}</span>
                                  </div>
                                );
                              }
                              return '\u00A0';
                            } else {
                              return renderPrice(cellOdds, player[`${selectedMarket}_${book}_direction`]);
                            }
                          })()}
                        </td>
                        );
                      })}
                    </tr>
                  </React.Fragment>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      {/* Mobile Book Pagination */}
      {isMobile && !isCollapsed && totalPages > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '15px',
          marginTop: '10px'
        }}>
          <button
            onClick={() => setMobileBookIndex(Math.max(0, mobileBookIndex - 1))}
            disabled={mobileBookIndex === 0}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: mobileBookIndex === 0 ? 'default' : 'pointer',
              opacity: mobileBookIndex === 0 ? 0.3 : 1,
              padding: '5px'
            }}
          >
            ‹
          </button>
          <div style={{ display: 'flex', gap: '8px' }}>
            {[...Array(totalPages)].map((_, index) => (
              <div
                key={index}
                onClick={() => setMobileBookIndex(index)}
                style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: index === mobileBookIndex ? '#007bff' : '#dee2e6',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s'
                }}
              />
            ))}
          </div>
          <button
            onClick={() => setMobileBookIndex(Math.min(totalPages - 1, mobileBookIndex + 1))}
            disabled={mobileBookIndex === totalPages - 1}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '20px',
              cursor: mobileBookIndex === totalPages - 1 ? 'default' : 'pointer',
              opacity: mobileBookIndex === totalPages - 1 ? 0.3 : 1,
              padding: '5px'
            }}
          >
            ›
          </button>
        </div>
      )}

      {lastUpdate && !(isMobile && isCollapsed) && (
        <div className="last-updated" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ flex: 1, textAlign: 'left', paddingLeft: '10px' }}>
            {safeGame.sport === 'mlb' && (() => {
              const awayHasLineup = safeGame.awayStartingLineup && safeGame.awayStartingLineup.length > 0;
              const homeHasLineup = safeGame.homeStartingLineup && safeGame.homeStartingLineup.length > 0;
              
              if (awayHasLineup && homeHasLineup) {
                return 'Lineups confirmed';
              } else if (awayHasLineup) {
                return `${safeGame.away} lineup confirmed`;
              } else if (homeHasLineup) {
                return `${safeGame.home} lineup confirmed`;
              } else {
                return 'Lineups not confirmed';
              }
            })()}
            {safeGame.sport !== 'mlb' && '* Confirmed Starter'}
          </div>
          <div style={{ flex: 1, textAlign: 'right' }}>
            Last updated: {formatLastUpdated(lastUpdate)} ET
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;